import { useState, useEffect, useRef, useMemo } from 'react';
import type { InactivityMessageTemplate } from '@coaching/api/db/schema.js';
import { useProject } from '../context';
import { trpc } from '../lib/trpc';
import { parseInactivityMessageTemplatesResult } from '../lib/inactivity';
import { DailyMessagePreviewModal } from '../components/DailyMessagePreviewModal';

/**
 * Communication schedule type from the API.
 */
interface CommunicationSchedule {
  type: PlanType;
  days?: DayOfWeek[];
  dataSources?: string[];
  phases?: Phase[];
  messageLength?: MessageLength;
  /** Default time for sending messages in HH:MM format (24-hour). */
  defaultTime?: string;
  /** Default timezone for sending messages (IANA identifier). */
  defaultTimezone?: string;
  /** Guided curriculum configuration (for guided_curriculum type). */
  guidedCurriculum?: GuidedCurriculum;
}

/**
 * Phase definition for phased journey plans.
 */
interface Phase {
  id: string;
  name: string;
  duration?: number;
  messageTemplate?: string;
  systemPromptAddition?: string;
  goals?: string[];
  evaluationCriteria?: string;
  pointThreshold?: number;
  maxDailyPoints?: number;
  order?: number;
}

/**
 * Communication plan types available in the Communication Plan page.
 */
type PlanType = 'simple_daily' | 'weekly_rotating' | 'phased_journey' | 'guided_curriculum';

/**
 * Progression mode for guided curriculum.
 */
type CurriculumProgressionMode = 'sequential' | 'flexible' | 'time_based' | 'mastery_based';

/**
 * Time calculation mode for guided curriculum.
 */
type TimeCalculationMode = 'calendar_days' | 'active_days';

/**
 * Content type for curriculum topic content blocks.
 */
type CurriculumContentType = 'text' | 'video' | 'audio' | 'exercise' | 'reflection' | 'resource';

/**
 * Content block within a curriculum topic.
 */
interface CurriculumContentBlock {
  id: string;
  type: CurriculumContentType;
  title?: string;
  content: string;
  durationMinutes?: number;
  mediaUrl?: string;
  required?: boolean;
  order: number;
}

/**
 * Curriculum topic representing a single learning unit.
 */
interface CurriculumTopic {
  id: string;
  title: string;
  description?: string;
  order: number;
  contentBlocks: CurriculumContentBlock[];
  messageTemplate?: string;
  instructions?: string;
  icon?: string;
}

/**
 * Phase unlock criteria.
 */
interface PhaseUnlockCriteria {
  type: 'previous_phase_complete' | 'days_elapsed' | 'topics_completed' | 'custom';
  daysRequired?: number;
  topicsRequired?: number;
}

/**
 * Curriculum phase containing multiple topics.
 */
interface CurriculumPhase {
  id: string;
  name: string;
  description?: string;
  order: number;
  topics: CurriculumTopic[];
  durationDays?: number;
  unlockCriteria?: PhaseUnlockCriteria;
  introductionMessage?: string;
  completionMessage?: string;
}

/**
 * Guided curriculum configuration.
 */
interface GuidedCurriculum {
  id: string;
  name: string;
  description?: string;
  progressionMode: CurriculumProgressionMode;
  timeCalculationMode?: TimeCalculationMode;
  phases: CurriculumPhase[];
  welcomeMessage?: string;
  completionMessage?: string;
  enableDailyMessages?: boolean;
  allowSkipping?: boolean;
  showProgress?: boolean;
}

/**
 * Days of the week for the communication schedule.
 */
type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

/**
 * Message length options for AI-generated messages.
 */
type MessageLength = 'short' | 'medium' | 'long';

/**
 * Day configuration for display.
 */
interface DayConfig {
  id: DayOfWeek;
  label: string;
  shortLabel: string;
}

/**
 * Available days of the week with their configurations.
 */
const DAYS_OF_WEEK: DayConfig[] = [
  { id: 'mon', label: 'Monday', shortLabel: 'Mon' },
  { id: 'tue', label: 'Tuesday', shortLabel: 'Tue' },
  { id: 'wed', label: 'Wednesday', shortLabel: 'Wed' },
  { id: 'thu', label: 'Thursday', shortLabel: 'Thu' },
  { id: 'fri', label: 'Friday', shortLabel: 'Fri' },
  { id: 'sat', label: 'Saturday', shortLabel: 'Sat' },
  { id: 'sun', label: 'Sunday', shortLabel: 'Sun' },
];

/**
 * Plan type configuration for display.
 */
interface PlanTypeConfig {
  id: PlanType;
  label: string;
  icon: string;
  description: string;
  details: string[];
}

/**
 * Available communication plan types with their configurations.
 */
const PLAN_TYPES: PlanTypeConfig[] = [
  {
    id: 'simple_daily',
    label: 'Simple Daily',
    icon: '📅',
    description: 'Send the same message at the same time every day',
    details: [
      'Single message template',
      'Fixed daily schedule',
      'Best for consistent reminders',
    ],
  },
  {
    id: 'weekly_rotating',
    label: 'Weekly Rotating',
    icon: '🔄',
    description: 'Rotate through different messages each day of the week',
    details: [
      'Up to 7 different templates',
      'Day-specific content',
      'Best for variety and engagement',
    ],
  },
  {
    id: 'phased_journey',
    label: 'Phased Journey',
    icon: '🚀',
    description: 'Guide users through a multi-phase communication journey',
    details: [
      'Multiple phases with progression',
      'Milestone-based transitions',
      'Best for onboarding and education',
    ],
  },
  {
    id: 'guided_curriculum',
    label: 'Guided Curriculum',
    icon: '📚',
    description: 'Structured learning curriculum with phases, topics, and daily focus',
    details: [
      'Multi-phase curriculum with topics',
      'Topic-specific AI instructions',
      'Flexible progression modes',
    ],
  },
];

/**
 * Progression mode options for guided curriculum.
 */
interface ProgressionModeConfig {
  id: CurriculumProgressionMode;
  label: string;
  description: string;
  icon: string;
}

const PROGRESSION_MODES: ProgressionModeConfig[] = [
  {
    id: 'sequential',
    label: 'Sequential',
    description: 'User must complete topics in order',
    icon: '📋',
  },
  {
    id: 'flexible',
    label: 'Flexible',
    description: 'User can access any topic anytime',
    icon: '🔀',
  },
  {
    id: 'time_based',
    label: 'Time-Based',
    description: 'Topics unlock based on time intervals',
    icon: '⏰',
  },
  {
    id: 'mastery_based',
    label: 'Mastery-Based',
    description: 'Topics unlock based on completion criteria',
    icon: '🎯',
  },
];

/**
 * Time calculation mode options.
 */
interface TimeCalculationModeConfig {
  id: TimeCalculationMode;
  label: string;
  description: string;
}

const TIME_CALCULATION_MODES: TimeCalculationModeConfig[] = [
  {
    id: 'calendar_days',
    label: 'Calendar Days',
    description: 'Count all days since curriculum start',
  },
  {
    id: 'active_days',
    label: 'Active Days',
    description: 'Count only days with message activity',
  },
];

/**
 * Message length configuration for display.
 */
interface MessageLengthConfig {
  id: MessageLength;
  label: string;
  description: string;
  wordCount: string;
}

/**
 * Available message length options with their configurations.
 */
const MESSAGE_LENGTHS: MessageLengthConfig[] = [
  {
    id: 'short',
    label: 'Short',
    description: 'Brief and to the point',
    wordCount: '~50 words',
  },
  {
    id: 'medium',
    label: 'Medium',
    description: 'Balanced detail and brevity',
    wordCount: '~100 words',
  },
  {
    id: 'long',
    label: 'Long',
    description: 'Detailed and elaborate',
    wordCount: '~200 words',
  },
];

/**
 * Data source configuration for message generation.
 */
interface DataSourceConfig {
  id: string;
  label: string;
  description: string;
  icon: string;
}

/**
 * Available data sources that admins can toggle for message generation.
 */
const DATA_SOURCES: DataSourceConfig[] = [
  {
    id: 'user_preferences',
    label: 'User Preferences',
    description: 'Include user-specific preferences and settings',
    icon: '⚙️',
  },
  {
    id: 'tool_outputs',
    label: 'Tool Outputs',
    description: 'Include outputs from enabled tools (cycle tracker, mood tracker, etc.)',
    icon: '🔧',
  },
  {
    id: 'media_library',
    label: 'Media Library',
    description: 'Allow AI to include images, GIFs, or other media from the library',
    icon: '🖼️',
  },
  {
    id: 'user_analytics',
    label: 'User Analytics',
    description: 'Include user engagement metrics and behavioral insights',
    icon: '📈',
  },
  {
    id: 'previous_day_summary',
    label: 'Previous Day Summary',
    description: 'Include summary of user activity from the previous day',
    icon: '📊',
  },
  {
    id: 'conversation_history',
    label: 'Conversation History',
    description: 'Reference recent conversation context for continuity',
    icon: '💬',
  },
  {
    id: 'external_calendar',
    label: 'External Calendar Events',
    description: 'Include upcoming calendar events if connected',
    icon: '📅',
  },
];

/**
 * Common timezones for the timezone selector.
 */
interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
}

/**
 * List of common IANA timezones for the dropdown.
 */
const COMMON_TIMEZONES: TimezoneOption[] = [
  { value: 'America/New_York', label: 'Eastern Time (US)', offset: 'UTC-5/UTC-4' },
  { value: 'America/Chicago', label: 'Central Time (US)', offset: 'UTC-6/UTC-5' },
  { value: 'America/Denver', label: 'Mountain Time (US)', offset: 'UTC-7/UTC-6' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)', offset: 'UTC-8/UTC-7' },
  { value: 'America/Anchorage', label: 'Alaska Time', offset: 'UTC-9/UTC-8' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time', offset: 'UTC-10' },
  { value: 'Europe/London', label: 'London (GMT/BST)', offset: 'UTC+0/UTC+1' },
  { value: 'Europe/Paris', label: 'Central European Time', offset: 'UTC+1/UTC+2' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)', offset: 'UTC+1/UTC+2' },
  { value: 'Europe/Moscow', label: 'Moscow Time', offset: 'UTC+3' },
  { value: 'Asia/Dubai', label: 'Dubai (Gulf Standard)', offset: 'UTC+4' },
  { value: 'Asia/Kolkata', label: 'India Standard Time', offset: 'UTC+5:30' },
  { value: 'Asia/Singapore', label: 'Singapore Time', offset: 'UTC+8' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time', offset: 'UTC+9' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)', offset: 'UTC+10/UTC+11' },
  { value: 'Pacific/Auckland', label: 'New Zealand Time', offset: 'UTC+12/UTC+13' },
];

/**
 * Variable definition for the sidebar.
 */
interface VariableDefinition {
  name: string;
  description: string;
  example?: string;
}

/**
 * Variable category grouping related variables.
 */
interface VariableCategory {
  id: string;
  label: string;
  icon: string;
  variables: VariableDefinition[];
}

/**
 * Tool variable schema mapping tool IDs to their available variables.
 */
const TOOL_VARIABLE_SCHEMAS: Record<string, { label: string; icon: string; variables: VariableDefinition[] }> = {
  cycle_tracker: {
    label: 'Cycle Tracker',
    icon: '🌸',
    variables: [
      { name: 'tools.cycle_tracker.phase', description: 'Current cycle phase', example: 'follicular' },
      { name: 'tools.cycle_tracker.day', description: 'Day of current cycle', example: '14' },
      { name: 'tools.cycle_tracker.nextPeriod', description: 'Days until next period', example: '12' },
      { name: 'tools.cycle_tracker.fertile', description: 'Is in fertile window', example: 'true' },
    ],
  },
  moon_phase: {
    label: 'Moon Phase',
    icon: '🌙',
    variables: [
      { name: 'tools.moon_phase.phase', description: 'Current moon phase name', example: 'Waxing Gibbous' },
      { name: 'tools.moon_phase.illumination', description: 'Moon illumination percentage', example: '78%' },
      { name: 'tools.moon_phase.emoji', description: 'Moon phase emoji', example: '🌔' },
      { name: 'tools.moon_phase.nextFull', description: 'Days until next full moon', example: '5' },
    ],
  },
  birth_chart: {
    label: 'Birth Chart',
    icon: '✨',
    variables: [
      { name: 'tools.birth_chart.sunSign', description: "User's sun sign", example: 'Scorpio' },
      { name: 'tools.birth_chart.moonSign', description: "User's moon sign", example: 'Pisces' },
      { name: 'tools.birth_chart.risingSign', description: "User's rising sign", example: 'Leo' },
      { name: 'tools.birth_chart.dailyGuidance', description: 'Personalized daily guidance', example: 'Focus on...' },
    ],
  },
  streak_tracker: {
    label: 'Streak Tracker',
    icon: '🔥',
    variables: [
      { name: 'tools.streak_tracker.currentStreak', description: 'Current streak count', example: '7' },
      { name: 'tools.streak_tracker.longestStreak', description: 'Longest streak achieved', example: '21' },
      { name: 'tools.streak_tracker.fireMode', description: 'Is in fire mode', example: 'true' },
      { name: 'tools.streak_tracker.lastCheckIn', description: 'Last check-in date', example: 'Yesterday' },
    ],
  },
  focus_timer: {
    label: 'Focus Timer',
    icon: '⏱️',
    variables: [
      { name: 'tools.focus_timer.sessionsToday', description: 'Focus sessions completed today', example: '3' },
      { name: 'tools.focus_timer.totalMinutes', description: 'Total focus minutes today', example: '75' },
      { name: 'tools.focus_timer.currentBlock', description: 'Current block status', example: 'break' },
    ],
  },
  mood_tracker: {
    label: 'Mood Tracker',
    icon: '😊',
    variables: [
      { name: 'tools.mood_tracker.currentMood', description: 'Most recent mood entry', example: 'happy' },
      { name: 'tools.mood_tracker.weeklyAvg', description: 'Weekly mood average', example: '3.8' },
      { name: 'tools.mood_tracker.trend', description: 'Mood trend direction', example: 'improving' },
    ],
  },
  meme_reward: {
    label: 'Meme Reward',
    icon: '🎉',
    variables: [
      { name: 'tools.meme_reward.earned', description: 'Has earned a meme reward', example: 'true' },
      { name: 'tools.meme_reward.totalEarned', description: 'Total memes earned', example: '12' },
    ],
  },
  vision_analyzer: {
    label: 'Vision Analyzer',
    icon: '📷',
    variables: [
      { name: 'tools.vision_analyzer.lastAnalysis', description: 'Summary of last analysis', example: 'Clean desk...' },
      { name: 'tools.vision_analyzer.verified', description: 'Last task verification status', example: 'true' },
    ],
  },
};

/**
 * Core variables always available regardless of enabled tools.
 */
const CORE_VARIABLES: VariableCategory = {
  id: 'core',
  label: 'Core Variables',
  icon: '📦',
  variables: [
    { name: 'user.name', description: "User's display name", example: 'Sarah' },
    { name: 'user.firstName', description: "User's first name", example: 'Sarah' },
    { name: 'user.timezone', description: "User's timezone", example: 'America/New_York' },
    { name: 'timeOfDay', description: 'Greeting based on time', example: 'morning' },
    { name: 'dayOfWeek', description: 'Current day name', example: 'Monday' },
    { name: 'date', description: 'Formatted current date', example: 'January 15' },
  ],
};

/**
 * Sample data values for template interpolation in the preview.
 */
const SAMPLE_DATA: Record<string, string> = {
  'user.name': 'Sarah',
  'user.firstName': 'Sarah',
  'user.timezone': 'America/New_York',
  'timeOfDay': 'morning',
  'dayOfWeek': 'Monday',
  'date': 'February 24',
  // Cycle Tracker
  'tools.cycle_tracker.phase': 'follicular',
  'tools.cycle_tracker.day': '14',
  'tools.cycle_tracker.nextPeriod': '12',
  'tools.cycle_tracker.fertile': 'true',
  // Moon Phase
  'tools.moon_phase.phase': 'Waxing Gibbous',
  'tools.moon_phase.illumination': '78%',
  'tools.moon_phase.emoji': '🌔',
  'tools.moon_phase.nextFull': '5',
  // Birth Chart
  'tools.birth_chart.sunSign': 'Scorpio',
  'tools.birth_chart.moonSign': 'Pisces',
  'tools.birth_chart.risingSign': 'Leo',
  'tools.birth_chart.dailyGuidance': 'Focus on self-care today',
  // Streak Tracker
  'tools.streak_tracker.currentStreak': '7',
  'tools.streak_tracker.longestStreak': '21',
  'tools.streak_tracker.fireMode': 'true',
  'tools.streak_tracker.lastCheckIn': 'Yesterday',
  // Focus Timer
  'tools.focus_timer.sessionsToday': '3',
  'tools.focus_timer.totalMinutes': '75',
  'tools.focus_timer.currentBlock': 'break',
  // Mood Tracker
  'tools.mood_tracker.currentMood': 'happy',
  'tools.mood_tracker.weeklyAvg': '3.8',
  'tools.mood_tracker.trend': 'improving',
  // Meme Reward
  'tools.meme_reward.earned': 'true',
  'tools.meme_reward.totalEarned': '12',
  // Vision Analyzer
  'tools.vision_analyzer.lastAnalysis': 'Clean desk setup',
  'tools.vision_analyzer.verified': 'true',
};

/**
 * Interpolate template variables with sample data.
 * Replaces {{variable}} patterns with their sample values.
 */
function interpolateTemplate(template: string): string {
  return template.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
    const trimmedName = variableName.trim();
    return SAMPLE_DATA[trimmedName] || match;
  });
}

/**
 * MobilePreview component props.
 */
interface MobilePreviewProps {
  text: string;
}

/**
 * MobilePreview component displays a CSS-based smartphone frame
 * with the interpolated message preview inside.
 */
function MobilePreview({ text }: MobilePreviewProps) {
  const interpolatedText = interpolateTemplate(text);
  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  return (
    <div style={mobileStyles.container}>
      {/* Phone Frame */}
      <div style={mobileStyles.phoneFrame}>
        {/* Status Bar */}
        <div style={mobileStyles.statusBar}>
          <span style={mobileStyles.statusTime}>{currentTime}</span>
          <div style={mobileStyles.statusIcons}>
            <span style={mobileStyles.statusIcon}>📶</span>
            <span style={mobileStyles.statusIcon}>🔋</span>
          </div>
        </div>

        {/* Notch */}
        <div style={mobileStyles.notch}>
          <div style={mobileStyles.notchCamera}></div>
        </div>

        {/* App Header */}
        <div style={mobileStyles.appHeader}>
          <div style={mobileStyles.appHeaderBack}>←</div>
          <div style={mobileStyles.appHeaderInfo}>
            <div style={mobileStyles.appHeaderAvatar}>🤖</div>
            <div style={mobileStyles.appHeaderText}>
              <span style={mobileStyles.appHeaderName}>HabitBot</span>
              <span style={mobileStyles.appHeaderStatus}>online</span>
            </div>
          </div>
          <div style={mobileStyles.appHeaderActions}>⋮</div>
        </div>

        {/* Chat Area */}
        <div style={mobileStyles.chatArea}>
          {interpolatedText ? (
            <div style={mobileStyles.messageBubble}>
              <p style={mobileStyles.messageText}>{interpolatedText}</p>
              <span style={mobileStyles.messageTime}>Just now</span>
            </div>
          ) : (
            <div style={mobileStyles.emptyState}>
              <span style={mobileStyles.emptyStateIcon}>💬</span>
              <span style={mobileStyles.emptyStateText}>
                Start typing to see your message preview
              </span>
            </div>
          )}
        </div>

        {/* Input Bar */}
        <div style={mobileStyles.inputBar}>
          <div style={mobileStyles.inputField}>Type a message...</div>
          <div style={mobileStyles.sendButton}>➤</div>
        </div>

        {/* Home Indicator */}
        <div style={mobileStyles.homeIndicator}></div>
      </div>
    </div>
  );
}

/**
 * Styles for the MobilePreview component.
 */
const mobileStyles: Record<string, React.CSSProperties> = {
  container: {
    flex: '0 0 300px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    borderRadius: '16px',
    border: '2px solid rgba(93, 86, 78, 0.08)',
  },
  phoneFrame: {
    width: '260px',
    height: '520px',
    backgroundColor: '#1a1a1a',
    borderRadius: '36px',
    padding: '8px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  },
  statusBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 20px 4px',
    backgroundColor: '#075E54',
    borderTopLeftRadius: '28px',
    borderTopRightRadius: '28px',
  },
  statusTime: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'white',
  },
  statusIcons: {
    display: 'flex',
    gap: '4px',
  },
  statusIcon: {
    fontSize: '10px',
  },
  notch: {
    position: 'absolute',
    top: '8px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '24px',
    backgroundColor: '#1a1a1a',
    borderRadius: '0 0 16px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  notchCamera: {
    width: '10px',
    height: '10px',
    backgroundColor: '#333',
    borderRadius: '50%',
    border: '2px solid #222',
  },
  appHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    backgroundColor: '#075E54',
  },
  appHeaderBack: {
    fontSize: '18px',
    color: 'white',
    fontWeight: 300,
  },
  appHeaderInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
  },
  appHeaderAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
  },
  appHeaderText: {
    display: 'flex',
    flexDirection: 'column',
  },
  appHeaderName: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
  },
  appHeaderStatus: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.7)',
  },
  appHeaderActions: {
    fontSize: '18px',
    color: 'white',
    padding: '4px 8px',
  },
  chatArea: {
    flex: 1,
    backgroundColor: '#ECE5DD',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23d4cfc4\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    padding: '12px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  messageBubble: {
    maxWidth: '85%',
    backgroundColor: '#DCF8C6',
    borderRadius: '8px',
    borderTopLeftRadius: '0',
    padding: '8px 12px',
    marginBottom: '4px',
    alignSelf: 'flex-start',
    boxShadow: '0 1px 1px rgba(0, 0, 0, 0.1)',
    position: 'relative',
  },
  messageText: {
    fontSize: '13px',
    lineHeight: 1.4,
    color: '#303030',
    margin: 0,
    wordWrap: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  messageTime: {
    fontSize: '10px',
    color: 'rgba(0, 0, 0, 0.45)',
    display: 'block',
    textAlign: 'right',
    marginTop: '4px',
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  emptyStateIcon: {
    fontSize: '32px',
    opacity: 0.5,
  },
  emptyStateText: {
    fontSize: '11px',
    color: '#888',
    textAlign: 'center',
    maxWidth: '150px',
    lineHeight: 1.4,
  },
  inputBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    backgroundColor: '#f0f0f0',
    borderBottomLeftRadius: '28px',
    borderBottomRightRadius: '28px',
  },
  inputField: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: '20px',
    padding: '8px 14px',
    fontSize: '12px',
    color: '#999',
  },
  sendButton: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#075E54',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '14px',
  },
  homeIndicator: {
    position: 'absolute',
    bottom: '4px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100px',
    height: '4px',
    backgroundColor: '#666',
    borderRadius: '2px',
  },
};

/**
 * Test message state for tracking the send operation.
 */
interface TestMessageState {
  isOpen: boolean;
  userId: string;
  isSending: boolean;
  result: { success: boolean; message: string } | null;
}

/**
 * Save feedback state for the save operation.
 */
interface SaveFeedback {
  type: 'success' | 'error' | null;
  message: string;
}

/**
 * Default phases for phased journey plans.
 */
const DEFAULT_PHASES: Phase[] = [
  { id: 'welcome', name: 'Welcome', pointThreshold: 3, maxDailyPoints: 1, order: 0, goals: [], messageTemplate: '' },
  { id: 'build_habit', name: 'Build Habit', pointThreshold: 7, maxDailyPoints: 1, order: 1, goals: [], messageTemplate: '' },
  { id: 'maintain', name: 'Maintain', pointThreshold: 14, maxDailyPoints: 1, order: 2, goals: [], messageTemplate: '' },
];

/**
 * Default day templates for weekly rotating plans.
 */
const DEFAULT_WEEKLY_TEMPLATES: Record<string, string> = {
  mon: '',
  tue: '',
  wed: '',
  thu: '',
  fri: '',
  sat: '',
  sun: '',
};

/**
 * Generate a unique ID for new curriculum items.
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Default curriculum topic for new topics.
 */
function createDefaultTopic(order: number): CurriculumTopic {
  return {
    id: generateId(),
    title: `Topic ${order + 1}`,
    description: '',
    order,
    contentBlocks: [],
    messageTemplate: '',
    instructions: '',
  };
}

/**
 * Default curriculum phase for new phases.
 */
function createDefaultCurriculumPhase(order: number): CurriculumPhase {
  return {
    id: generateId(),
    name: `Phase ${order + 1}`,
    description: '',
    order,
    topics: [createDefaultTopic(0)],
    durationDays: 7,
  };
}

/**
 * Default guided curriculum configuration.
 */
function createDefaultGuidedCurriculum(): GuidedCurriculum {
  return {
    id: generateId(),
    name: 'My Curriculum',
    description: '',
    progressionMode: 'sequential',
    timeCalculationMode: 'calendar_days',
    phases: [createDefaultCurriculumPhase(0)],
    enableDailyMessages: true,
    showProgress: true,
  };
}

/**
 * Communication Plan page component.
 *
 * Provides tools for creating and managing communication plans with
 * different scheduling strategies, including a prompt editor with
 * an available variables sidebar.
 */
export function CommunicationPlanPage() {
  const { currentBotId, currentBot } = useProject();
  const utils = trpc.useUtils();
  const [selectedPlanType, setSelectedPlanType] = useState<PlanType | null>(null);
  const [selectedDays, setSelectedDays] = useState<Set<DayOfWeek>>(new Set());
  const [promptText, setPromptText] = useState('');
  const [enabledDataSources, setEnabledDataSources] = useState<Set<string>>(new Set());
  const [selectedMessageLength, setSelectedMessageLength] = useState<MessageLength | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [testMessage, setTestMessage] = useState<TestMessageState>({
    isOpen: false,
    userId: '',
    isSending: false,
    result: null,
  });
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // Multi-prompt state for phased/weekly plans
  const [promptTemplates, setPromptTemplates] = useState<Record<string, string>>({});
  const [phases, setPhases] = useState<Phase[]>([]);
  const [activePromptTab, setActivePromptTab] = useState<string>('default');
  const [isInitialized, setIsInitialized] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<SaveFeedback>({ type: null, message: '' });
  const [inactivityMessageTemplates, setInactivityMessageTemplates] = useState<InactivityMessageTemplate[]>([]);
  const [inactivityTemplateError, setInactivityTemplateError] = useState<string | null>(null);

  // Default message timing state
  const [defaultTime, setDefaultTime] = useState<string>('09:00');
  const [defaultTimezone, setDefaultTimezone] = useState<string>('');

  // Guided curriculum state
  const [guidedCurriculum, setGuidedCurriculum] = useState<GuidedCurriculum | null>(null);
  const [selectedCurriculumPhaseId, setSelectedCurriculumPhaseId] = useState<string | null>(null);
  const [selectedCurriculumTopicId, setSelectedCurriculumTopicId] = useState<string | null>(null);
  const [expandedPhaseIds, setExpandedPhaseIds] = useState<Set<string>>(new Set());

  // Fetch full bot data with enabledTools
  const botQuery = trpc.bots.get.useQuery(
    { id: currentBotId! },
    { enabled: !!currentBotId }
  );

  // Update bot mutation
  const updateMutation = trpc.bots.update.useMutation({
    onSuccess: () => {
      utils.bots.get.invalidate({ id: currentBotId! });
      setSaveFeedback({ type: 'success', message: 'Changes saved successfully!' });
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveFeedback({ type: null, message: '' });
      }, 3000);
    },
    onError: (error) => {
      setSaveFeedback({ type: 'error', message: error.message || 'Failed to save changes' });
    },
  });

  // Get enabled tools from the fetched bot data
  const enabledTools: string[] = botQuery.data?.enabledTools && Array.isArray(botQuery.data.enabledTools)
    ? botQuery.data.enabledTools
    : [];

  // Test daily message mutation
  const testDailyMessageMutation = trpc.bots.testDailyMessage.useMutation({
    onSuccess: (data) => {
      setTestMessage((prev) => ({
        ...prev,
        isSending: false,
        result: {
          success: true,
          message: `Message sent successfully! Content: "${data.content?.substring(0, 100)}${(data.content?.length ?? 0) > 100 ? '...' : ''}"`,
        },
      }));
    },
    onError: (error) => {
      setTestMessage((prev) => ({
        ...prev,
        isSending: false,
        result: {
          success: false,
          message: error.message || 'Failed to send test message',
        },
      }));
    },
  });

  // Initialize state from bot data when it loads
  useEffect(() => {
    if (botQuery.data && !isInitialized) {
      const schedule = botQuery.data.communicationSchedule as CommunicationSchedule | null;

      // Initialize plan type
      if (schedule?.type) {
        setSelectedPlanType(schedule.type);
      }

      const inactivityTemplatesResult = parseInactivityMessageTemplatesResult(botQuery.data.inactivityMessageTemplates);
      setInactivityMessageTemplates(
        inactivityTemplatesResult.templates.length > 0
          ? [...inactivityTemplatesResult.templates].sort((a, b) => a.day - b.day)
          : []
      );
      setInactivityTemplateError(
        inactivityTemplatesResult.isInvalid
          ? 'Stored inactive-user WhatsApp template config is invalid. Add replacement rows, or save with no rows to clear it.'
          : null
      );

      // Initialize days
      if (schedule?.days && Array.isArray(schedule.days)) {
        setSelectedDays(new Set(schedule.days));
      }

      // Initialize data sources
      if (schedule?.dataSources && Array.isArray(schedule.dataSources)) {
        setEnabledDataSources(new Set(schedule.dataSources));
      }

      // Initialize message length
      if (schedule?.messageLength) {
        setSelectedMessageLength(schedule.messageLength);
      }

      // Initialize default time and timezone
      if (schedule?.defaultTime) {
        setDefaultTime(schedule.defaultTime);
      }
      if (schedule?.defaultTimezone) {
        setDefaultTimezone(schedule.defaultTimezone);
      }

      // Initialize prompt templates based on plan type
      if (schedule?.type === 'guided_curriculum' && schedule.guidedCurriculum) {
        // Initialize guided curriculum
        setGuidedCurriculum(schedule.guidedCurriculum);
        const firstPhase = schedule.guidedCurriculum.phases[0];
        if (firstPhase) {
          setSelectedCurriculumPhaseId(firstPhase.id);
          setExpandedPhaseIds(new Set([firstPhase.id]));
          const firstTopic = firstPhase.topics[0];
          if (firstTopic) {
            setSelectedCurriculumTopicId(firstTopic.id);
            setPromptText(firstTopic.messageTemplate || '');
          }
        }
      } else if (schedule?.type === 'phased_journey' && schedule.phases) {
        // Initialize phases and their templates, migrating old format if needed
        const phasesData = schedule.phases.map((phase, index) => ({
          ...phase,
          order: phase.order ?? index,
          pointThreshold: phase.pointThreshold ?? phase.duration ?? 7,
          maxDailyPoints: phase.maxDailyPoints ?? 1,
          goals: phase.goals ?? [],
          systemPromptAddition: phase.systemPromptAddition ?? '',
          evaluationCriteria: phase.evaluationCriteria ?? '',
        }));
        setPhases(phasesData);
        const templates: Record<string, string> = {
          default: botQuery.data.dailyMessageTemplate || '',
        };
        phasesData.forEach((phase) => {
          templates[phase.id] = phase.messageTemplate || '';
        });
        setPromptTemplates(templates);
        // Set active tab to default
        setActivePromptTab('default');
        setPromptText(botQuery.data.dailyMessageTemplate || '');
      } else if (schedule?.type === 'weekly_rotating') {
        // Initialize weekly templates from phases (days are stored as phases)
        const templates: Record<string, string> = {
          ...DEFAULT_WEEKLY_TEMPLATES,
          default: botQuery.data.dailyMessageTemplate || '',
        };
        if (schedule.phases) {
          schedule.phases.forEach((phase) => {
            templates[phase.id] = phase.messageTemplate || '';
          });
        }
        setPromptTemplates(templates);
        setActivePromptTab('default');
        setPromptText(botQuery.data.dailyMessageTemplate || '');
      } else {
        // Simple daily - use dailyMessageTemplate or first phase template
        const template = botQuery.data.dailyMessageTemplate ||
          (schedule?.phases?.[0]?.messageTemplate) || '';
        setPromptText(template);
        setPromptTemplates({ default: template });
        setActivePromptTab('default');
      }

      setIsInitialized(true);
    }
  }, [botQuery.data, isInitialized]);

  /**
   * Build variable categories based on enabled tools.
   */
  const variableCategories: VariableCategory[] = [
    CORE_VARIABLES,
    ...enabledTools
      .filter((toolId) => TOOL_VARIABLE_SCHEMAS[toolId])
      .map((toolId) => ({
        id: toolId,
        label: TOOL_VARIABLE_SCHEMAS[toolId].label,
        icon: TOOL_VARIABLE_SCHEMAS[toolId].icon,
        variables: TOOL_VARIABLE_SCHEMAS[toolId].variables,
      })),
  ];

  /**
   * Toggle a day's selection state.
   */
  const toggleDay = (day: DayOfWeek) => {
    setSelectedDays((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(day)) {
        newSet.delete(day);
      } else {
        newSet.add(day);
      }
      return newSet;
    });
  };

  /**
   * Toggle a data source's enabled state.
   */
  const toggleDataSource = (dataSourceId: string) => {
    setEnabledDataSources((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dataSourceId)) {
        newSet.delete(dataSourceId);
      } else {
        newSet.add(dataSourceId);
      }
      return newSet;
    });
  };

  /**
   * Insert a variable at the cursor position in the textarea.
   */
  const insertVariable = (variableName: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = promptText;
    const variable = `{{${variableName}}}`;

    const newText = text.substring(0, start) + variable + text.substring(end);
    setPromptText(newText);

    // Restore focus and cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + variable.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  /**
   * Send a test daily message to a user.
   */
  const handleSendTestMessage = () => {
    if (!currentBotId || !testMessage.userId.trim()) return;

    setTestMessage((prev) => ({ ...prev, isSending: true, result: null }));
    testDailyMessageMutation.mutate({
      botId: currentBotId,
      userId: testMessage.userId.trim(),
    });
  };

  /**
   * Open the test message dialog.
   */
  const openTestMessageDialog = () => {
    setTestMessage({
      isOpen: true,
      userId: '',
      isSending: false,
      result: null,
    });
  };

  /**
   * Close the test message dialog.
   */
  const closeTestMessageDialog = () => {
    setTestMessage({
      isOpen: false,
      userId: '',
      isSending: false,
      result: null,
    });
  };

  /**
   * Get current prompt templates with active prompt text synced.
   */
  const getCurrentTemplates = (): Record<string, string> => {
    return {
      ...promptTemplates,
      [activePromptTab]: promptText,
    };
  };

  /**
   * Handle plan type change and initialize appropriate templates.
   */
  const handlePlanTypeChange = (planType: PlanType) => {
    // Sync current prompt text to templates before switching
    const currentTemplates = getCurrentTemplates();
    setPromptTemplates(currentTemplates);

    setSelectedPlanType(planType);

    if (planType === 'guided_curriculum') {
      // Initialize with default curriculum if none exists
      if (!guidedCurriculum) {
        const defaultCurriculum = createDefaultGuidedCurriculum();
        setGuidedCurriculum(defaultCurriculum);
        const firstPhase = defaultCurriculum.phases[0];
        if (firstPhase) {
          setSelectedCurriculumPhaseId(firstPhase.id);
          setExpandedPhaseIds(new Set([firstPhase.id]));
          const firstTopic = firstPhase.topics[0];
          if (firstTopic) {
            setSelectedCurriculumTopicId(firstTopic.id);
            setPromptText(firstTopic.messageTemplate || '');
          }
        }
      } else {
        const firstPhase = guidedCurriculum.phases[0];
        if (firstPhase) {
          setSelectedCurriculumPhaseId(firstPhase.id);
          setExpandedPhaseIds(new Set([firstPhase.id]));
          const firstTopic = firstPhase.topics[0];
          if (firstTopic) {
            setSelectedCurriculumTopicId(firstTopic.id);
            setPromptText(firstTopic.messageTemplate || '');
          }
        }
      }
    } else if (planType === 'phased_journey') {
      // Initialize with default phases if no phases exist
      const defaultTemplate = currentTemplates['default'] || promptText || '';
      if (phases.length === 0) {
        setPhases(DEFAULT_PHASES);
        const templates: Record<string, string> = { default: defaultTemplate };
        DEFAULT_PHASES.forEach((phase) => {
          templates[phase.id] = '';
        });
        setPromptTemplates(templates);
        setActivePromptTab('default');
        setPromptText(defaultTemplate);
      } else {
        setPromptTemplates((prev) => ({ ...prev, default: defaultTemplate }));
        setActivePromptTab('default');
        setPromptText(defaultTemplate);
      }
    } else if (planType === 'weekly_rotating') {
      // Initialize weekly templates if not present
      const defaultTemplate = currentTemplates['default'] || promptText || '';
      const templates: Record<string, string> = { ...DEFAULT_WEEKLY_TEMPLATES, default: defaultTemplate };
      Object.keys(currentTemplates).forEach((key) => {
        if (key in templates) {
          templates[key] = currentTemplates[key];
        }
      });
      setPromptTemplates(templates);
      setActivePromptTab('default');
      setPromptText(defaultTemplate);
    } else {
      // Simple daily - use single template
      const defaultTemplate = currentTemplates['default'] ||
        currentTemplates[activePromptTab] ||
        promptText || '';
      setPromptTemplates({ default: defaultTemplate });
      setActivePromptTab('default');
      setPromptText(defaultTemplate);
    }
  };

  /**
   * Handle switching between prompt tabs.
   */
  const handlePromptTabChange = (tabId: string) => {
    // Save current prompt text to templates
    setPromptTemplates((prev) => ({
      ...prev,
      [activePromptTab]: promptText,
    }));

    // Switch to new tab
    setActivePromptTab(tabId);
    setPromptText(promptTemplates[tabId] || '');
  };

  // ============== PHASED JOURNEY HANDLERS ==============

  /**
   * Update a phase field.
   */
  const updatePhase = (phaseId: string, updates: Partial<Phase>) => {
    setPhases(prev => prev.map(p => p.id === phaseId ? { ...p, ...updates } : p));
  };

  /**
   * Add a new phase.
   */
  const addPhase = () => {
    const newId = `phase_${Date.now()}`;
    const newOrder = phases.length;
    const newPhase: Phase = {
      id: newId,
      name: `Phase ${newOrder + 1}`,
      pointThreshold: 7,
      maxDailyPoints: 1,
      order: newOrder,
      goals: [],
      systemPromptAddition: '',
      evaluationCriteria: '',
      messageTemplate: '',
    };
    setPhases(prev => [...prev, newPhase]);
    setPromptTemplates(prev => ({ ...prev, [newId]: '' }));
    setActivePromptTab(newId);
    setPromptText('');
  };

  /**
   * Remove a phase.
   */
  const removePhase = (phaseId: string) => {
    setPhases(prev => {
      const filtered = prev.filter(p => p.id !== phaseId);
      // Re-order remaining phases
      return filtered.map((p, i) => ({ ...p, order: i }));
    });
    setPromptTemplates(prev => {
      const next = { ...prev };
      delete next[phaseId];
      return next;
    });
    // Switch active tab if we removed the active one
    if (activePromptTab === phaseId) {
      const remaining = phases.filter(p => p.id !== phaseId);
      if (remaining.length > 0) {
        setActivePromptTab(remaining[0].id);
        setPromptText(promptTemplates[remaining[0].id] || '');
      }
    }
  };

  /**
   * Add a goal to a phase.
   */
  const addPhaseGoal = (phaseId: string) => {
    setPhases(prev => prev.map(p => {
      if (p.id !== phaseId) return p;
      return { ...p, goals: [...(p.goals || []), ''] };
    }));
  };

  /**
   * Update a goal in a phase.
   */
  const updatePhaseGoal = (phaseId: string, goalIndex: number, value: string) => {
    setPhases(prev => prev.map(p => {
      if (p.id !== phaseId) return p;
      const goals = [...(p.goals || [])];
      goals[goalIndex] = value;
      return { ...p, goals };
    }));
  };

  /**
   * Remove a goal from a phase.
   */
  const removePhaseGoal = (phaseId: string, goalIndex: number) => {
    setPhases(prev => prev.map(p => {
      if (p.id !== phaseId) return p;
      const goals = [...(p.goals || [])];
      goals.splice(goalIndex, 1);
      return { ...p, goals };
    }));
  };

  /**
   * Move a phase up or down in the list.
   */
  const movePhase = (phaseId: string, direction: 'up' | 'down') => {
    setPhases(prev => {
      const index = prev.findIndex(p => p.id === phaseId);
      if (index < 0) return prev;
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      const newPhases = [...prev];
      [newPhases[index], newPhases[newIndex]] = [newPhases[newIndex], newPhases[index]];
      return newPhases.map((p, i) => ({ ...p, order: i }));
    });
  };

  // ============== GUIDED CURRICULUM HANDLERS ==============

  /**
   * Toggle phase expansion.
   */
  const togglePhaseExpanded = (phaseId: string) => {
    setExpandedPhaseIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(phaseId)) {
        newSet.delete(phaseId);
      } else {
        newSet.add(phaseId);
      }
      return newSet;
    });
  };

  /**
   * Select a curriculum topic.
   */
  const selectCurriculumTopic = (phaseId: string, topicId: string) => {
    // Sync current prompt text to previous topic before switching
    if (guidedCurriculum && selectedCurriculumPhaseId && selectedCurriculumTopicId) {
      syncPromptToCurrentTopic();
    }

    // Find the topic and set its template as prompt text
    const phase = guidedCurriculum?.phases.find(p => p.id === phaseId);
    const topic = phase?.topics.find(t => t.id === topicId);

    setSelectedCurriculumPhaseId(phaseId);
    setSelectedCurriculumTopicId(topicId);
    setPromptText(topic?.messageTemplate || '');
  };

  /**
   * Add a new phase to the curriculum.
   */
  const addCurriculumPhase = () => {
    if (!guidedCurriculum) return;

    const newPhase = createDefaultCurriculumPhase(guidedCurriculum.phases.length);
    setGuidedCurriculum({
      ...guidedCurriculum,
      phases: [...guidedCurriculum.phases, newPhase],
    });
    setExpandedPhaseIds(prev => new Set([...prev, newPhase.id]));
    setSelectedCurriculumPhaseId(newPhase.id);
    setSelectedCurriculumTopicId(newPhase.topics[0]?.id || null);
    setPromptText(newPhase.topics[0]?.messageTemplate || '');
  };

  /**
   * Delete a phase from the curriculum.
   */
  const deleteCurriculumPhase = (phaseId: string) => {
    if (!guidedCurriculum || guidedCurriculum.phases.length <= 1) return;

    const newPhases = guidedCurriculum.phases
      .filter(p => p.id !== phaseId)
      .map((p, idx) => ({ ...p, order: idx }));

    setGuidedCurriculum({
      ...guidedCurriculum,
      phases: newPhases,
    });

    // If deleted the selected phase, select the first available
    if (selectedCurriculumPhaseId === phaseId && newPhases.length > 0) {
      const firstPhase = newPhases[0];
      setSelectedCurriculumPhaseId(firstPhase.id);
      setSelectedCurriculumTopicId(firstPhase.topics[0]?.id || null);
      setPromptText(firstPhase.topics[0]?.messageTemplate || '');
    }
  };

  /**
   * Update a phase's properties.
   */
  const updateCurriculumPhase = (phaseId: string, updates: Partial<CurriculumPhase>) => {
    if (!guidedCurriculum) return;

    setGuidedCurriculum({
      ...guidedCurriculum,
      phases: guidedCurriculum.phases.map(p =>
        p.id === phaseId ? { ...p, ...updates } : p
      ),
    });
  };

  /**
   * Add a new topic to a phase.
   */
  const addCurriculumTopic = (phaseId: string) => {
    if (!guidedCurriculum) return;

    const phase = guidedCurriculum.phases.find(p => p.id === phaseId);
    if (!phase) return;

    const newTopic = createDefaultTopic(phase.topics.length);
    setGuidedCurriculum({
      ...guidedCurriculum,
      phases: guidedCurriculum.phases.map(p =>
        p.id === phaseId
          ? { ...p, topics: [...p.topics, newTopic] }
          : p
      ),
    });

    setSelectedCurriculumPhaseId(phaseId);
    setSelectedCurriculumTopicId(newTopic.id);
    setPromptText('');
  };

  /**
   * Delete a topic from a phase.
   */
  const deleteCurriculumTopic = (phaseId: string, topicId: string) => {
    if (!guidedCurriculum) return;

    const phase = guidedCurriculum.phases.find(p => p.id === phaseId);
    if (!phase || phase.topics.length <= 1) return;

    const newTopics = phase.topics
      .filter(t => t.id !== topicId)
      .map((t, idx) => ({ ...t, order: idx }));

    setGuidedCurriculum({
      ...guidedCurriculum,
      phases: guidedCurriculum.phases.map(p =>
        p.id === phaseId ? { ...p, topics: newTopics } : p
      ),
    });

    // If deleted the selected topic, select the first available
    if (selectedCurriculumTopicId === topicId && newTopics.length > 0) {
      setSelectedCurriculumTopicId(newTopics[0].id);
      setPromptText(newTopics[0].messageTemplate || '');
    }
  };

  /**
   * Update a topic's properties.
   */
  const updateCurriculumTopic = (phaseId: string, topicId: string, updates: Partial<CurriculumTopic>) => {
    if (!guidedCurriculum) return;

    setGuidedCurriculum({
      ...guidedCurriculum,
      phases: guidedCurriculum.phases.map(p =>
        p.id === phaseId
          ? {
              ...p,
              topics: p.topics.map(t =>
                t.id === topicId ? { ...t, ...updates } : t
              ),
            }
          : p
      ),
    });
  };

  /**
   * Update the guided curriculum settings.
   */
  const updateCurriculumSettings = (updates: Partial<GuidedCurriculum>) => {
    if (!guidedCurriculum) return;
    setGuidedCurriculum({ ...guidedCurriculum, ...updates });
  };

  /**
   * Get the currently selected topic.
   */
  const getSelectedTopic = (): CurriculumTopic | null => {
    if (!guidedCurriculum || !selectedCurriculumPhaseId || !selectedCurriculumTopicId) return null;
    const phase = guidedCurriculum.phases.find(p => p.id === selectedCurriculumPhaseId);
    return phase?.topics.find(t => t.id === selectedCurriculumTopicId) || null;
  };

  // ============== END GUIDED CURRICULUM HANDLERS ==============

  /**
   * Build phases array from prompt templates for saving.
   */
  const buildPhasesForSave = (): Phase[] => {
    const templates = getCurrentTemplates();

    if (selectedPlanType === 'phased_journey') {
      return phases.map((phase) => ({
        ...phase,
        messageTemplate: templates[phase.id] || '',
      }));
    } else if (selectedPlanType === 'weekly_rotating') {
      // Store day templates as phases (exclude 'default' which is dailyMessageTemplate)
      return DAYS_OF_WEEK.map((day) => ({
        id: day.id,
        name: day.label,
        messageTemplate: templates[day.id] || '',
      }));
    } else {
      // Simple daily - single phase with the template
      return [{
        id: 'default',
        name: 'Daily Message',
        messageTemplate: templates['default'] || promptText,
      }];
    }
  };

  /**
   * Sync the current prompt text to the selected curriculum topic.
   */
  const syncPromptToCurrentTopic = () => {
    if (!guidedCurriculum || !selectedCurriculumPhaseId || !selectedCurriculumTopicId) return;

    setGuidedCurriculum(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        phases: prev.phases.map(phase => {
          if (phase.id !== selectedCurriculumPhaseId) return phase;
          return {
            ...phase,
            topics: phase.topics.map(topic => {
              if (topic.id !== selectedCurriculumTopicId) return topic;
              return { ...topic, messageTemplate: promptText };
            }),
          };
        }),
      };
    });
  };

  /**
   * Build the current draft communication schedule, including unsaved UI state.
   */
  const buildDraftCommunicationSchedule = (): CommunicationSchedule | null => {
    if (!selectedPlanType) {
      return null;
    }

    return {
      type: selectedPlanType,
      days: Array.from(selectedDays),
      dataSources: Array.from(enabledDataSources),
      phases: selectedPlanType === 'guided_curriculum' ? undefined : buildPhasesForSave(),
      messageLength: selectedMessageLength || undefined,
      defaultTime: defaultTime || undefined,
      defaultTimezone: defaultTimezone || undefined,
      guidedCurriculum: selectedPlanType === 'guided_curriculum'
        ? (guidedCurriculum ? {
            ...guidedCurriculum,
            phases: guidedCurriculum.phases.map(phase => ({
              ...phase,
              topics: phase.topics.map(topic =>
                (phase.id === selectedCurriculumPhaseId && topic.id === selectedCurriculumTopicId)
                  ? { ...topic, messageTemplate: promptText }
                  : topic
              ),
            })),
          } : undefined)
        : undefined,
    };
  };

  /**
   * Handle saving all Communication Plan configuration.
   */
  const handleSave = () => {
    if (!currentBotId || !selectedPlanType) return;

    const validatedInactivityTemplates = validateInactivityMessageTemplates();
    if (!validatedInactivityTemplates) return;

    // Clear previous feedback
    setSaveFeedback({ type: null, message: '' });

    // For guided curriculum, sync the current prompt text to the topic first
    if (selectedPlanType === 'guided_curriculum' && guidedCurriculum && selectedCurriculumPhaseId && selectedCurriculumTopicId) {
      syncPromptToCurrentTopic();
    }

    const communicationSchedule = buildDraftCommunicationSchedule();

    if (!communicationSchedule) {
      return;
    }

    // Update dailyMessageTemplate from the "Default" tab
    const templates = getCurrentTemplates();
    const dailyMessageTemplate = (selectedPlanType === 'simple_daily' || selectedPlanType === 'phased_journey' || selectedPlanType === 'weekly_rotating')
      ? (templates['default'] || promptText)
      : undefined;

    updateMutation.mutate({
      id: currentBotId,
      communicationSchedule,
      dailyMessageTemplate,
      inactivityMessageTemplates: validatedInactivityTemplates,
    });
  };

  const previewCommunicationSchedule = useMemo(
    () => buildDraftCommunicationSchedule(),
    [
      activePromptTab,
      defaultTime,
      defaultTimezone,
      enabledDataSources,
      guidedCurriculum,
      phases,
      promptTemplates,
      promptText,
      selectedCurriculumPhaseId,
      selectedCurriculumTopicId,
      selectedDays,
      selectedMessageLength,
      selectedPlanType,
    ]
  );

  const validateInactivityMessageTemplates = (): InactivityMessageTemplate[] | null => {
    const seenDays = new Set<number>();
    const templates: InactivityMessageTemplate[] = [];

    for (const template of inactivityMessageTemplates) {
      if (!Number.isInteger(template.day) || template.day <= 0) {
        setInactivityTemplateError('Inactive user template days must be positive whole numbers.');
        return null;
      }

      if (!template.templateName.trim()) {
        setInactivityTemplateError('Each inactive user template needs a WhatsApp template name.');
        return null;
      }

      if (seenDays.has(template.day)) {
        setInactivityTemplateError(`Only one WhatsApp template can be configured for day ${template.day}.`);
        return null;
      }

      seenDays.add(template.day);
      templates.push({
        day: template.day,
        templateName: template.templateName.trim(),
        languageCode: template.languageCode?.trim() || undefined,
        bodyParameters: template.bodyParameters
          ?.map((parameter) => parameter.trim())
          .filter(Boolean),
      });
    }

    setInactivityTemplateError(null);
    return templates.sort((a, b) => a.day - b.day);
  };

  const updateInactivityMessageTemplate = (
    index: number,
    field: keyof InactivityMessageTemplate,
    value: string | number | string[] | undefined,
  ) => {
    setInactivityMessageTemplates((prev) => prev.map((template, templateIndex) => (
      templateIndex === index ? { ...template, [field]: value } : template
    )));
    setInactivityTemplateError(null);
  };

  const addInactivityMessageTemplate = () => {
    setInactivityMessageTemplates((prev) => [
      ...prev,
      { day: nextInactivityTemplateDay(prev), templateName: '', languageCode: 'en' },
    ]);
    setInactivityTemplateError(null);
  };

  const removeInactivityMessageTemplate = (index: number) => {
    setInactivityMessageTemplates((prev) => prev.filter((_, templateIndex) => templateIndex !== index));
    setInactivityTemplateError(null);
  };

  const nextInactivityTemplateDay = (templates: InactivityMessageTemplate[]): number => {
    const usedDays = new Set(templates.map((template) => template.day));
    let day = 1;
    while (usedDays.has(day)) day += 1;
    return day;
  };

  const formatBodyParameters = (bodyParameters: string[] | undefined): string => (
    bodyParameters?.join('\n') ?? ''
  );

  const parseBodyParameters = (value: string): string[] | undefined => {
    const parameters = value
      .split('\n')
      .map((parameter) => parameter.trim())
      .filter(Boolean);

    return parameters.length > 0 ? parameters : undefined;
  };

  /**
   * Get the available prompt tabs based on plan type.
   */
  const promptTabs = useMemo(() => {
    const defaultTab = { id: 'default', label: 'Default', icon: '📝' };
    if (selectedPlanType === 'phased_journey') {
      return [
        defaultTab,
        ...phases.map((phase) => ({
          id: phase.id,
          label: phase.name,
          icon: phase.id === 'welcome' ? '👋' : phase.id === 'build_habit' ? '💪' : '🏆',
        })),
      ];
    } else if (selectedPlanType === 'weekly_rotating') {
      return [
        defaultTab,
        ...DAYS_OF_WEEK.map((day) => ({
          id: day.id,
          label: day.shortLabel,
          icon: '',
        })),
      ];
    }
    return [];
  }, [selectedPlanType, phases]);

  /**
   * Determine if prompt tabs should be shown.
   */
  const showPromptTabs = selectedPlanType === 'phased_journey' || selectedPlanType === 'weekly_rotating';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Communication Plan</h1>
          <p style={styles.description}>
            Create and manage communication plans for your users.
            {currentBot && (
              <span style={styles.botInfo}> Currently editing: {currentBot.name}</span>
            )}
          </p>
        </div>
        <div style={styles.headerActions}>
          <button
            onClick={openTestMessageDialog}
            disabled={!currentBotId}
            style={{
              ...styles.sendTestButton,
              ...(currentBotId ? {} : styles.sendTestButtonDisabled),
            }}
          >
            Send Test Message
          </button>
          <button
            onClick={handleSave}
            disabled={!currentBotId || !selectedPlanType || updateMutation.isPending}
            style={{
              ...styles.saveButton,
              ...(!currentBotId || !selectedPlanType || updateMutation.isPending
                ? styles.saveButtonDisabled
                : {}),
            }}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Save Feedback Toast */}
      {saveFeedback.type && (
        <div
          style={{
            ...styles.feedbackToast,
            ...(saveFeedback.type === 'success' ? styles.feedbackToastSuccess : styles.feedbackToastError),
          }}
        >
          <span>{saveFeedback.type === 'success' ? '✓' : '✕'}</span>
          <span>{saveFeedback.message}</span>
          <button
            onClick={() => setSaveFeedback({ type: null, message: '' })}
            style={styles.feedbackCloseButton}
          >
            ×
          </button>
        </div>
      )}

      {/* Test Message Dialog */}
      {testMessage.isOpen && (
        <div style={styles.dialogOverlay} onClick={closeTestMessageDialog}>
          <div style={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.dialogHeader}>
              <h3 style={styles.dialogTitle}>Send Test Message</h3>
              <button onClick={closeTestMessageDialog} style={styles.dialogCloseButton}>
                x
              </button>
            </div>
            <div style={styles.dialogBody}>
              <p style={styles.dialogDescription}>
                Send a test daily message to a specific user to verify the AI generation and delivery pipeline.
              </p>
              <label style={styles.dialogLabel}>
                User ID
                <input
                  type="text"
                  value={testMessage.userId}
                  onChange={(e) => setTestMessage((prev) => ({ ...prev, userId: e.target.value, result: null }))}
                  placeholder="Enter user ID (UUID)"
                  style={styles.dialogInput}
                  disabled={testMessage.isSending}
                />
              </label>
              {testMessage.result && (
                <div
                  style={{
                    ...styles.dialogResult,
                    ...(testMessage.result.success ? styles.dialogResultSuccess : styles.dialogResultError),
                  }}
                >
                  {testMessage.result.message}
                </div>
              )}
            </div>
            <div style={styles.dialogFooter}>
              <button onClick={closeTestMessageDialog} style={styles.dialogCancelButton}>
                Cancel
              </button>
              <button
                onClick={handleSendTestMessage}
                disabled={!testMessage.userId.trim() || testMessage.isSending}
                style={{
                  ...styles.dialogSendButton,
                  ...(!testMessage.userId.trim() || testMessage.isSending ? styles.dialogSendButtonDisabled : {}),
                }}
              >
                {testMessage.isSending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DailyMessagePreviewModal
        isOpen={isPreviewModalOpen}
        botId={currentBotId ?? null}
        dailyMessageTemplate={promptText}
        communicationSchedule={previewCommunicationSchedule ?? undefined}
        onClose={() => setIsPreviewModalOpen(false)}
      />

      {/* Plan Type Selector */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Select Communication Plan Type</h2>
          <p style={styles.sectionDescription}>
            Choose how you want to schedule and deliver messages to your users.
          </p>
        </div>

        <div style={styles.planTypeGrid}>
          {PLAN_TYPES.map((planType) => {
            const isSelected = selectedPlanType === planType.id;
            return (
              <button
                key={planType.id}
                onClick={() => handlePlanTypeChange(planType.id)}
                style={{
                  ...styles.planTypeCard,
                  ...(isSelected ? styles.planTypeCardSelected : {}),
                }}
              >
                <div style={styles.planTypeHeader}>
                  <span style={styles.planTypeIcon}>{planType.icon}</span>
                  <span style={styles.planTypeLabel}>{planType.label}</span>
                  {isSelected && (
                    <span style={styles.selectedBadge}>Selected</span>
                  )}
                </div>
                <p style={styles.planTypeDescription}>{planType.description}</p>
                <ul style={styles.planTypeDetails}>
                  {planType.details.map((detail, index) => (
                    <li key={index} style={styles.planTypeDetail}>
                      <span style={styles.detailBullet}>•</span>
                      {detail}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      </div>

      {/* Daily Check-in Message Group */}
      <div style={styles.groupHeading}>
        <h2 style={styles.groupHeadingTitle}>Daily Check-in Message</h2>
        <p style={styles.groupHeadingDescription}>
          Configure how the bot composes and delivers the daily check-in message sent to each user.
        </p>
      </div>

      {/* Check-in Days Picker */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Check-in Days</h2>
          <p style={styles.sectionDescription}>
            Select which days of the week the bot should send daily check-in messages.
          </p>
        </div>

        <div style={styles.daysContainer}>
          {DAYS_OF_WEEK.map((day) => {
            const isSelected = selectedDays.has(day.id);
            return (
              <button
                key={day.id}
                onClick={() => toggleDay(day.id)}
                style={{
                  ...styles.dayButton,
                  ...(isSelected ? styles.dayButtonSelected : {}),
                }}
                title={day.label}
                aria-pressed={isSelected}
                aria-label={`${day.label}${isSelected ? ' (selected)' : ''}`}
              >
                <span style={{
                  ...styles.dayLabel,
                  ...(isSelected ? styles.dayLabelSelected : {}),
                }}>
                  {day.shortLabel}
                </span>
                {isSelected && (
                  <span style={styles.dayCheckmark}>✓</span>
                )}
              </button>
            );
          })}
        </div>

        {selectedDays.size > 0 && (
          <p style={styles.daysSelectedInfo}>
            {selectedDays.size} day{selectedDays.size !== 1 ? 's' : ''} selected
          </p>
        )}

        {/* Default Message Time Configuration */}
        <div style={styles.defaultTimingContainer}>
          <h3 style={styles.defaultTimingTitle}>Default Message Time</h3>
          <p style={styles.defaultTimingDescription}>
            Set a default time and timezone for sending scheduled messages. This applies when users don&apos;t have their own preference set.
          </p>
          <div style={styles.defaultTimingInputs}>
            <div style={styles.timingInputGroup}>
              <label htmlFor="defaultTime" style={styles.timingLabel}>
                Time (24-hour format)
              </label>
              <input
                id="defaultTime"
                type="time"
                value={defaultTime}
                onChange={(e) => setDefaultTime(e.target.value)}
                style={styles.timeInput}
              />
            </div>
            <div style={styles.timingInputGroup}>
              <label htmlFor="defaultTimezone" style={styles.timingLabel}>
                Timezone
              </label>
              <select
                id="defaultTimezone"
                value={defaultTimezone}
                onChange={(e) => setDefaultTimezone(e.target.value)}
                style={styles.timezoneSelect}
              >
                <option value="">Select a timezone...</option>
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label} ({tz.offset})
                  </option>
                ))}
              </select>
            </div>
          </div>
          {defaultTime && defaultTimezone && (
            <p style={styles.timingPreview}>
              Messages will be sent at {defaultTime} {COMMON_TIMEZONES.find(tz => tz.value === defaultTimezone)?.label || defaultTimezone}
            </p>
          )}
        </div>
      </div>

      {/* Inactive User WhatsApp Templates */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Inactive User WhatsApp Templates</h2>
          <p style={styles.sectionDescription}>
            Configure approved WhatsApp templates to send when a user has not messaged for an exact number of days.
          </p>
        </div>

        <div style={styles.inactivityTemplatesPanel}>
          {inactivityMessageTemplates.length === 0 ? (
            <p style={styles.emptyTemplateText}>No inactive-user WhatsApp templates configured.</p>
          ) : (
            <div style={styles.inactivityTemplatesTable}>
              <div style={styles.inactivityTemplateHeaderRow}>
                <span>Inactive Days</span>
                <span>Template Name</span>
                <span>Language</span>
                <span>Body Parameters</span>
                <span />
              </div>
              {inactivityMessageTemplates.map((template, index) => (
                <div
                  key={index}
                  style={styles.inactivityTemplateRow}
                >
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={template.day}
                    onChange={(event) => updateInactivityMessageTemplate(
                      index,
                      'day',
                      event.target.value ? Number.parseInt(event.target.value, 10) : 1
                    )}
                    style={styles.inactivityDayInput}
                  />
                  <input
                    type="text"
                    value={template.templateName}
                    onChange={(event) => updateInactivityMessageTemplate(index, 'templateName', event.target.value)}
                    placeholder="inactive_3_days"
                    style={styles.inactivityTemplateInput}
                  />
                  <input
                    type="text"
                    value={template.languageCode || ''}
                    onChange={(event) => updateInactivityMessageTemplate(index, 'languageCode', event.target.value || undefined)}
                    placeholder="en"
                    style={styles.inactivityLanguageInput}
                  />
                  <textarea
                    value={formatBodyParameters(template.bodyParameters)}
                    onChange={(event) => updateInactivityMessageTemplate(index, 'bodyParameters', parseBodyParameters(event.target.value))}
                    placeholder="{{user.name}}"
                    rows={2}
                    style={styles.inactivityParameterInput}
                  />
                  <button
                    type="button"
                    onClick={() => removeInactivityMessageTemplate(index)}
                    style={styles.inactivityRemoveButton}
                    aria-label={`Remove inactive user template for day ${template.day}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {inactivityTemplateError && (
            <p style={styles.inactivityTemplateError}>{inactivityTemplateError}</p>
          )}

          <button
            type="button"
            onClick={addInactivityMessageTemplate}
            style={styles.inactivityAddButton}
          >
            Add WhatsApp Template
          </button>
        </div>
      </div>

      {/* Check-in Data Sources Configuration */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Check-in Data Sources</h2>
          <p style={styles.sectionDescription}>
            Configure which data sources the AI should use when generating the check-in message.
          </p>
        </div>

        <div style={styles.dataSourcesGrid}>
          {DATA_SOURCES.map((dataSource) => {
            const isEnabled = enabledDataSources.has(dataSource.id);
            return (
              <label
                key={dataSource.id}
                style={{
                  ...styles.dataSourceItem,
                  ...(isEnabled ? styles.dataSourceItemEnabled : {}),
                }}
              >
                <div style={styles.dataSourceCheckboxWrapper}>
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => toggleDataSource(dataSource.id)}
                    style={styles.dataSourceCheckbox}
                  />
                </div>
                <div style={styles.dataSourceContent}>
                  <div style={styles.dataSourceHeader}>
                    <span style={styles.dataSourceIcon}>{dataSource.icon}</span>
                    <span style={{
                      ...styles.dataSourceLabel,
                      ...(isEnabled ? styles.dataSourceLabelEnabled : {}),
                    }}>
                      {dataSource.label}
                    </span>
                  </div>
                  <p style={styles.dataSourceDescription}>{dataSource.description}</p>
                </div>
              </label>
            );
          })}
        </div>

        {enabledDataSources.size > 0 && (
          <p style={styles.dataSourcesSelectedInfo}>
            {enabledDataSources.size} data source{enabledDataSources.size !== 1 ? 's' : ''} enabled
          </p>
        )}
      </div>

      {/* Check-in Message Length Selector */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Check-in Message Length</h2>
          <p style={styles.sectionDescription}>
            Choose the target length for check-in messages. This helps the AI calibrate response verbosity.
          </p>
        </div>

        <div style={styles.messageLengthContainer}>
          <select
            value={selectedMessageLength ?? ''}
            onChange={(e) => setSelectedMessageLength(e.target.value as MessageLength || null)}
            style={styles.messageLengthSelect}
          >
            <option value="">Default (concise)</option>
            {MESSAGE_LENGTHS.map((length) => (
              <option key={length.id} value={length.id}>
                {length.label} - {length.wordCount}
              </option>
            ))}
          </select>
          {selectedMessageLength && (
            <p style={styles.messageLengthDescription}>
              {MESSAGE_LENGTHS.find((l) => l.id === selectedMessageLength)?.description}
            </p>
          )}
        </div>
      </div>

      {/* Selected Plan Info */}
      {selectedPlanType && (
        <div style={styles.selectedInfo}>
          <div style={styles.selectedInfoContent}>
            <span style={styles.selectedInfoIcon}>
              {PLAN_TYPES.find((p) => p.id === selectedPlanType)?.icon}
            </span>
            <div>
              <p style={styles.selectedInfoTitle}>
                {PLAN_TYPES.find((p) => p.id === selectedPlanType)?.label} Plan Selected
              </p>
              <p style={styles.selectedInfoText}>
                Configure your plan settings below to get started.
              </p>
            </div>
          </div>
          <button
            onClick={() => setSelectedPlanType(null)}
            style={styles.clearButton}
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Phased Journey Phase Editor */}
      {selectedPlanType === 'phased_journey' && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Phase Configuration</h2>
            <p style={styles.sectionDescription}>
              Define phases for the user journey. Each phase has point thresholds, goals, and evaluation criteria.
            </p>
          </div>

          <div style={styles.phaseEditorContainer}>
            {phases.map((phase, index) => {
              const estimatedDays = phase.pointThreshold && phase.maxDailyPoints
                ? Math.ceil(phase.pointThreshold / phase.maxDailyPoints)
                : null;
              return (
                <div key={phase.id} style={styles.phaseCard}>
                  <div style={styles.phaseCardHeader}>
                    <div style={styles.phaseCardHeaderLeft}>
                      <span style={styles.phaseOrderBadge}>{index + 1}</span>
                      <input
                        type="text"
                        value={phase.name}
                        onChange={(e) => updatePhase(phase.id, { name: e.target.value })}
                        style={styles.phaseNameInput}
                        placeholder="Phase name"
                      />
                    </div>
                    <div style={styles.phaseCardHeaderActions}>
                      <button
                        onClick={() => movePhase(phase.id, 'up')}
                        disabled={index === 0}
                        style={{
                          ...styles.phaseReorderButton,
                          ...(index === 0 ? styles.phaseReorderButtonDisabled : {}),
                        }}
                        title="Move up"
                      >
                        &#x25B2;
                      </button>
                      <button
                        onClick={() => movePhase(phase.id, 'down')}
                        disabled={index === phases.length - 1}
                        style={{
                          ...styles.phaseReorderButton,
                          ...(index === phases.length - 1 ? styles.phaseReorderButtonDisabled : {}),
                        }}
                        title="Move down"
                      >
                        &#x25BC;
                      </button>
                      <button
                        onClick={() => removePhase(phase.id)}
                        style={styles.phaseDeleteButton}
                        title="Remove phase"
                      >
                        &times;
                      </button>
                    </div>
                  </div>

                  <div style={styles.phaseCardBody}>
                    {/* Point Threshold & Max Daily Points */}
                    <div style={styles.phaseFieldRow}>
                      <div style={styles.phaseFieldGroup}>
                        <label style={styles.phaseFieldLabel}>Point Threshold</label>
                        <input
                          type="number"
                          min={1}
                          value={phase.pointThreshold ?? ''}
                          onChange={(e) => updatePhase(phase.id, { pointThreshold: parseFloat(e.target.value) || 0 })}
                          style={styles.phaseFieldInput}
                          placeholder="e.g. 7"
                        />
                      </div>
                      <div style={styles.phaseFieldGroup}>
                        <label style={styles.phaseFieldLabel}>Max Daily Points</label>
                        <input
                          type="number"
                          min={0.1}
                          step={0.1}
                          value={phase.maxDailyPoints ?? ''}
                          onChange={(e) => updatePhase(phase.id, { maxDailyPoints: parseFloat(e.target.value) || 0 })}
                          style={styles.phaseFieldInput}
                          placeholder="e.g. 1 or 0.5"
                        />
                      </div>
                      <div style={styles.phaseFieldGroup}>
                        <label style={styles.phaseFieldLabel}>Order</label>
                        <input
                          type="number"
                          min={0}
                          value={phase.order ?? index}
                          onChange={(e) => updatePhase(phase.id, { order: parseInt(e.target.value) || 0 })}
                          style={styles.phaseFieldInput}
                        />
                      </div>
                    </div>

                    {estimatedDays !== null && (
                      <div style={styles.phaseEstimatedDays}>
                        Estimated ~{estimatedDays} day{estimatedDays !== 1 ? 's' : ''} to complete
                      </div>
                    )}

                    {/* System Prompt Addition */}
                    <div style={styles.phaseFieldFull}>
                      <label style={styles.phaseFieldLabel}>System Prompt Addition</label>
                      <textarea
                        value={phase.systemPromptAddition || ''}
                        onChange={(e) => updatePhase(phase.id, { systemPromptAddition: e.target.value })}
                        style={styles.phaseFieldTextarea}
                        placeholder="Additional system prompt instructions for this phase..."
                        rows={3}
                      />
                    </div>

                    {/* Goals */}
                    <div style={styles.phaseFieldFull}>
                      <label style={styles.phaseFieldLabel}>Goals</label>
                      {(phase.goals || []).map((goal, goalIndex) => (
                        <div key={goalIndex} style={styles.phaseGoalRow}>
                          <input
                            type="text"
                            value={goal}
                            onChange={(e) => updatePhaseGoal(phase.id, goalIndex, e.target.value)}
                            style={styles.phaseGoalInput}
                            placeholder={`Goal ${goalIndex + 1}`}
                          />
                          <button
                            onClick={() => removePhaseGoal(phase.id, goalIndex)}
                            style={styles.phaseGoalRemoveButton}
                            title="Remove goal"
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addPhaseGoal(phase.id)}
                        style={styles.phaseAddGoalButton}
                      >
                        + Add Goal
                      </button>
                    </div>

                    {/* Evaluation Criteria */}
                    <div style={styles.phaseFieldFull}>
                      <label style={styles.phaseFieldLabel}>Evaluation Criteria</label>
                      <textarea
                        value={phase.evaluationCriteria || ''}
                        onChange={(e) => updatePhase(phase.id, { evaluationCriteria: e.target.value })}
                        style={styles.phaseFieldTextarea}
                        placeholder="Criteria for evaluating phase completion..."
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            <button onClick={addPhase} style={styles.phaseAddButton}>
              + Add Phase
            </button>
          </div>
        </div>
      )}

      {/* Guided Curriculum Editor */}
      {selectedPlanType === 'guided_curriculum' && guidedCurriculum && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Curriculum Configuration</h2>
            <p style={styles.sectionDescription}>
              Define your curriculum structure with phases, topics, and daily focus areas.
            </p>
          </div>

          {/* Curriculum Settings */}
          <div style={styles.curriculumSettingsContainer}>
            <div style={styles.curriculumSettingsGrid}>
              {/* Curriculum Name */}
              <div style={styles.curriculumSettingItem}>
                <label style={styles.curriculumSettingLabel}>Curriculum Name</label>
                <input
                  type="text"
                  value={guidedCurriculum.name}
                  onChange={(e) => updateCurriculumSettings({ name: e.target.value })}
                  style={styles.curriculumSettingInput}
                  placeholder="My Learning Curriculum"
                />
              </div>

              {/* Progression Mode */}
              <div style={styles.curriculumSettingItem}>
                <label style={styles.curriculumSettingLabel}>Progression Mode</label>
                <select
                  value={guidedCurriculum.progressionMode}
                  onChange={(e) => updateCurriculumSettings({ progressionMode: e.target.value as CurriculumProgressionMode })}
                  style={styles.curriculumSettingSelect}
                >
                  {PROGRESSION_MODES.map(mode => (
                    <option key={mode.id} value={mode.id}>
                      {mode.icon} {mode.label} - {mode.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time Calculation Mode (for time-based progression) */}
              {guidedCurriculum.progressionMode === 'time_based' && (
                <div style={styles.curriculumSettingItem}>
                  <label style={styles.curriculumSettingLabel}>Time Calculation</label>
                  <div style={styles.timeCalcToggleContainer}>
                    {TIME_CALCULATION_MODES.map(mode => (
                      <button
                        key={mode.id}
                        onClick={() => updateCurriculumSettings({ timeCalculationMode: mode.id })}
                        style={{
                          ...styles.timeCalcToggleButton,
                          ...(guidedCurriculum.timeCalculationMode === mode.id ? styles.timeCalcToggleButtonActive : {}),
                        }}
                      >
                        <span style={styles.timeCalcToggleLabel}>{mode.label}</span>
                        <span style={styles.timeCalcToggleDesc}>{mode.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div style={{ ...styles.curriculumSettingItem, gridColumn: '1 / -1' }}>
                <label style={styles.curriculumSettingLabel}>Description (optional)</label>
                <textarea
                  value={guidedCurriculum.description || ''}
                  onChange={(e) => updateCurriculumSettings({ description: e.target.value })}
                  style={styles.curriculumSettingTextarea}
                  placeholder="A brief description of what this curriculum covers..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Phases and Topics Editor */}
          <div style={styles.curriculumEditorContainer}>
            {/* Phases List (Left Panel) */}
            <div style={styles.curriculumPhasesPanel}>
              <div style={styles.curriculumPanelHeader}>
                <span style={styles.curriculumPanelTitle}>Phases & Topics</span>
                <button
                  onClick={addCurriculumPhase}
                  style={styles.curriculumAddButton}
                  title="Add new phase"
                >
                  + Phase
                </button>
              </div>
              <div style={styles.curriculumPhasesList}>
                {guidedCurriculum.phases.map((phase) => (
                  <div key={phase.id} style={styles.curriculumPhaseItem}>
                    <div
                      style={{
                        ...styles.curriculumPhaseHeader,
                        ...(selectedCurriculumPhaseId === phase.id ? styles.curriculumPhaseHeaderSelected : {}),
                      }}
                      onClick={() => togglePhaseExpanded(phase.id)}
                    >
                      <span style={styles.curriculumPhaseExpandIcon}>
                        {expandedPhaseIds.has(phase.id) ? '▼' : '▶'}
                      </span>
                      <input
                        type="text"
                        value={phase.name}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateCurriculumPhase(phase.id, { name: e.target.value });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={styles.curriculumPhaseNameInput}
                        placeholder="Phase name"
                      />
                      <span style={styles.curriculumPhaseTopicCount}>
                        {phase.topics.length} topic{phase.topics.length !== 1 ? 's' : ''}
                      </span>
                      {guidedCurriculum.phases.length > 1 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCurriculumPhase(phase.id);
                          }}
                          style={styles.curriculumDeleteButton}
                          title="Delete phase"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    {expandedPhaseIds.has(phase.id) && (
                      <div style={styles.curriculumTopicsList}>
                        {/* Phase Duration (for time-based) */}
                        {guidedCurriculum.progressionMode === 'time_based' && (
                          <div style={styles.curriculumPhaseDuration}>
                            <label style={styles.curriculumPhaseDurationLabel}>Duration:</label>
                            <input
                              type="number"
                              value={phase.durationDays || 7}
                              onChange={(e) => updateCurriculumPhase(phase.id, { durationDays: parseInt(e.target.value) || 7 })}
                              style={styles.curriculumPhaseDurationInput}
                              min={1}
                            />
                            <span style={styles.curriculumPhaseDurationUnit}>days</span>
                          </div>
                        )}
                        {phase.topics.map((topic) => (
                          <div
                            key={topic.id}
                            onClick={() => selectCurriculumTopic(phase.id, topic.id)}
                            style={{
                              ...styles.curriculumTopicItem,
                              ...(selectedCurriculumTopicId === topic.id ? styles.curriculumTopicItemSelected : {}),
                            }}
                          >
                            <span style={styles.curriculumTopicIcon}>{topic.icon || '📖'}</span>
                            <span style={styles.curriculumTopicTitle}>{topic.title}</span>
                            {phase.topics.length > 1 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteCurriculumTopic(phase.id, topic.id);
                                }}
                                style={styles.curriculumDeleteButton}
                                title="Delete topic"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          onClick={() => addCurriculumTopic(phase.id)}
                          style={styles.curriculumAddTopicButton}
                        >
                          + Add Topic
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Topic Editor (Right Panel) */}
            <div style={styles.curriculumTopicEditorPanel}>
              {getSelectedTopic() ? (
                <>
                  <div style={styles.curriculumTopicEditorHeader}>
                    <h3 style={styles.curriculumTopicEditorTitle}>Edit Topic</h3>
                  </div>
                  <div style={styles.curriculumTopicEditorContent}>
                    {/* Topic Title */}
                    <div style={styles.curriculumTopicField}>
                      <label style={styles.curriculumTopicFieldLabel}>Topic Title</label>
                      <input
                        type="text"
                        value={getSelectedTopic()?.title || ''}
                        onChange={(e) => {
                          if (selectedCurriculumPhaseId && selectedCurriculumTopicId) {
                            updateCurriculumTopic(selectedCurriculumPhaseId, selectedCurriculumTopicId, { title: e.target.value });
                          }
                        }}
                        style={styles.curriculumTopicFieldInput}
                        placeholder="Enter topic title"
                      />
                    </div>

                    {/* Topic Icon */}
                    <div style={styles.curriculumTopicField}>
                      <label style={styles.curriculumTopicFieldLabel}>Icon (emoji)</label>
                      <input
                        type="text"
                        value={getSelectedTopic()?.icon || ''}
                        onChange={(e) => {
                          if (selectedCurriculumPhaseId && selectedCurriculumTopicId) {
                            updateCurriculumTopic(selectedCurriculumPhaseId, selectedCurriculumTopicId, { icon: e.target.value });
                          }
                        }}
                        style={{ ...styles.curriculumTopicFieldInput, width: '80px' }}
                        placeholder="📖"
                      />
                    </div>

                    {/* Topic Description */}
                    <div style={styles.curriculumTopicField}>
                      <label style={styles.curriculumTopicFieldLabel}>Description (optional)</label>
                      <textarea
                        value={getSelectedTopic()?.description || ''}
                        onChange={(e) => {
                          if (selectedCurriculumPhaseId && selectedCurriculumTopicId) {
                            updateCurriculumTopic(selectedCurriculumPhaseId, selectedCurriculumTopicId, { description: e.target.value });
                          }
                        }}
                        style={styles.curriculumTopicFieldTextarea}
                        placeholder="Brief description of what this topic covers..."
                        rows={2}
                      />
                    </div>

                    {/* AI Instructions */}
                    <div style={styles.curriculumTopicField}>
                      <label style={styles.curriculumTopicFieldLabel}>
                        AI Instructions
                        <span style={styles.curriculumTopicFieldHint}>
                          (Guide the AI's coaching behavior for this topic)
                        </span>
                      </label>
                      <textarea
                        value={getSelectedTopic()?.instructions || ''}
                        onChange={(e) => {
                          if (selectedCurriculumPhaseId && selectedCurriculumTopicId) {
                            updateCurriculumTopic(selectedCurriculumPhaseId, selectedCurriculumTopicId, { instructions: e.target.value });
                          }
                        }}
                        style={styles.curriculumTopicFieldTextarea}
                        placeholder="e.g., Focus on introducing basic concepts. Be encouraging and patient. Ask questions to check understanding..."
                        rows={4}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div style={styles.curriculumNoTopicSelected}>
                  <span style={styles.curriculumNoTopicIcon}>📚</span>
                  <span style={styles.curriculumNoTopicText}>Select a topic to edit its details</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Prompt Editor Section - Split Pane Layout */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Check-in Message Template</h2>
          <p style={styles.sectionDescription}>
            Write the check-in message template using variables to personalize content for each user.
          </p>
        </div>

        <div style={styles.splitPane}>
          {/* Left: Editor */}
          <div style={styles.editorPane}>
            {/* Prompt Tabs for Weekly/Phased Plans */}
            {showPromptTabs && (
              <div style={styles.promptTabsContainer}>
                {promptTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handlePromptTabChange(tab.id)}
                    style={{
                      ...styles.promptTab,
                      ...(activePromptTab === tab.id ? styles.promptTabActive : {}),
                    }}
                  >
                    {tab.icon && <span style={styles.promptTabIcon}>{tab.icon}</span>}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            )}
            <div style={styles.editorHeader}>
              <span style={styles.editorLabel}>
                {selectedPlanType === 'guided_curriculum' && getSelectedTopic()
                  ? `Message Template - ${getSelectedTopic()?.title || 'Topic'}`
                  : showPromptTabs
                    ? `Prompt Editor - ${promptTabs.find((t) => t.id === activePromptTab)?.label || 'Default'}`
                    : 'Prompt Editor'}
              </span>
              <span style={styles.characterCount}>{promptText.length} characters</span>
            </div>
            <textarea
              ref={textareaRef}
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="Good {{timeOfDay}}, {{user.name}}! How are you feeling today?"
              style={styles.editor}
              rows={12}
            />
            <div style={styles.editorFooter}>
              <span style={styles.editorHint}>
                Use {"{{variable}}"} syntax to insert dynamic content
              </span>
              <button
                onClick={() => setIsPreviewModalOpen(true)}
                disabled={!currentBotId}
                style={{
                  ...styles.previewLaunchButton,
                  ...(currentBotId ? {} : styles.previewLaunchButtonDisabled),
                }}
              >
                Preview Daily Check-In
              </button>
            </div>
          </div>

          {/* Center: Variables Sidebar */}
          <div style={styles.sidebarPane}>
            <div style={styles.sidebarHeader}>
              <span style={styles.sidebarLabel}>Available Variables</span>
              <span style={styles.variableCount}>
                {variableCategories.reduce((acc, cat) => acc + cat.variables.length, 0)} variables
              </span>
            </div>

            {botQuery.isLoading ? (
              <div style={styles.sidebarLoading}>Loading variables...</div>
            ) : (
              <div style={styles.variablesList}>
                {variableCategories.map((category) => (
                  <div key={category.id} style={styles.variableCategory}>
                    <div style={styles.categoryHeader}>
                      <span style={styles.categoryIcon}>{category.icon}</span>
                      <span style={styles.categoryLabel}>{category.label}</span>
                    </div>
                    <div style={styles.variableItems}>
                      {category.variables.map((variable) => (
                        <button
                          key={variable.name}
                          onClick={() => insertVariable(variable.name)}
                          style={styles.variableItem}
                          title={`Insert {{${variable.name}}}`}
                        >
                          <div style={styles.variableItemHeader}>
                            <code style={styles.variableName}>{`{{${variable.name}}}`}</code>
                            <span style={styles.insertIcon}>+</span>
                          </div>
                          <span style={styles.variableDescription}>{variable.description}</span>
                          {variable.example && (
                            <span style={styles.variableExample}>e.g., "{variable.example}"</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {enabledTools.length === 0 && (
                  <div style={styles.noToolsMessage}>
                    <span style={styles.noToolsIcon}>💡</span>
                    <span style={styles.noToolsText}>
                      Enable tools in Bot Settings to unlock more variables
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Mobile Preview */}
          <div style={styles.previewPane}>
            <div style={styles.previewHeader}>
              <span style={styles.previewLabel}>📱 Mobile Preview</span>
            </div>
            <MobilePreview text={promptText} />
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1500px',
    padding: '32px',
  },
  header: {
    marginBottom: '32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '28px',
    fontWeight: 400,
    color: '#3D3A36',
    margin: '0 0 8px 0',
  },
  description: {
    fontSize: '14px',
    color: '#706A62',
    margin: 0,
  },
  botInfo: {
    color: '#5A6A8A',
    fontWeight: 500,
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  saveButton: {
    padding: '10px 24px',
    borderRadius: '8px',
    backgroundColor: '#5A8A5E',
    border: 'none',
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
    flexShrink: 0,
  },
  saveButtonDisabled: {
    backgroundColor: '#C9BBA8',
    cursor: 'not-allowed',
  },
  feedbackToast: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: 500,
  },
  feedbackToastSuccess: {
    backgroundColor: 'rgba(90, 138, 94, 0.1)',
    color: '#5A8A5E',
    border: '1px solid rgba(90, 138, 94, 0.2)',
  },
  feedbackToastError: {
    backgroundColor: 'rgba(200, 80, 80, 0.1)',
    color: '#C85050',
    border: '1px solid rgba(200, 80, 80, 0.2)',
  },
  feedbackCloseButton: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    color: 'inherit',
    opacity: 0.7,
    padding: '0 4px',
  },
  promptTabsContainer: {
    display: 'flex',
    gap: '4px',
    padding: '12px 14px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    backgroundColor: 'rgba(93, 86, 78, 0.02)',
    flexWrap: 'wrap' as const,
  },
  promptTab: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '6px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: '#706A62',
    transition: 'all 0.15s ease',
  },
  promptTabActive: {
    backgroundColor: '#3D3A36',
    color: 'white',
    border: '1px solid #3D3A36',
  },
  promptTabIcon: {
    fontSize: '14px',
  },
  section: {
    marginBottom: '32px',
  },
  sectionHeader: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: '0 0 6px 0',
  },
  sectionDescription: {
    fontSize: '14px',
    color: '#9B9285',
    margin: 0,
  },
  planTypeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '20px',
  },
  planTypeCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '2px solid rgba(93, 86, 78, 0.08)',
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },
  planTypeCardSelected: {
    border: '2px solid #3D3A36',
    boxShadow: '0 4px 12px rgba(61, 58, 54, 0.1)',
  },
  planTypeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  planTypeIcon: {
    fontSize: '24px',
  },
  planTypeLabel: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#3D3A36',
    flex: 1,
  },
  selectedBadge: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#5A8A5E',
    backgroundColor: 'rgba(90, 138, 94, 0.15)',
    padding: '4px 8px',
    borderRadius: '6px',
  },
  planTypeDescription: {
    fontSize: '14px',
    color: '#5C564E',
    margin: '0 0 16px 0',
    lineHeight: 1.5,
  },
  planTypeDetails: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  planTypeDetail: {
    fontSize: '13px',
    color: '#9B9285',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  detailBullet: {
    color: '#C9BBA8',
  },
  selectedInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderRadius: '12px',
    backgroundColor: 'rgba(90, 138, 94, 0.08)',
    border: '1px solid rgba(90, 138, 94, 0.15)',
    marginBottom: '24px',
  },
  selectedInfoContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  selectedInfoIcon: {
    fontSize: '28px',
  },
  selectedInfoTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: '0 0 2px 0',
  },
  selectedInfoText: {
    fontSize: '13px',
    color: '#706A62',
    margin: 0,
  },
  clearButton: {
    padding: '8px 16px',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    fontSize: '13px',
    fontWeight: 500,
    color: '#5C564E',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  daysContainer: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  dayButton: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    width: '72px',
    height: '72px',
    borderRadius: '12px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
    position: 'relative' as const,
  },
  dayButtonSelected: {
    border: '2px solid #3D3A36',
    backgroundColor: '#3D3A36',
    boxShadow: '0 2px 8px rgba(61, 58, 54, 0.15)',
  },
  dayLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#5C564E',
  },
  dayLabelSelected: {
    color: 'white',
  },
  dayCheckmark: {
    position: 'absolute' as const,
    top: '6px',
    right: '6px',
    fontSize: '10px',
    color: 'white',
    backgroundColor: 'rgba(90, 138, 94, 0.9)',
    borderRadius: '50%',
    width: '16px',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  daysSelectedInfo: {
    marginTop: '16px',
    fontSize: '13px',
    color: '#706A62',
    margin: '16px 0 0 0',
  },
  // Default Timing Styles
  defaultTimingContainer: {
    marginTop: '28px',
    padding: '20px 24px',
    backgroundColor: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  defaultTimingTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: '0 0 6px 0',
  },
  defaultTimingDescription: {
    fontSize: '13px',
    color: '#9B9285',
    margin: '0 0 16px 0',
    lineHeight: 1.5,
  },
  defaultTimingInputs: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap' as const,
  },
  timingInputGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    minWidth: '200px',
  },
  timingLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#5C564E',
  },
  timeInput: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Mono", monospace',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
    transition: 'border-color 0.15s ease',
    width: '140px',
  },
  timezoneSelect: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#3D3A36',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.15s ease',
    appearance: 'none' as const,
    backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%235C564E\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '16px',
    paddingRight: '40px',
    minWidth: '280px',
  },
  timingPreview: {
    marginTop: '14px',
    fontSize: '13px',
    color: '#5A8A5E',
    fontWeight: 500,
    margin: '14px 0 0 0',
  },
  inactivityTemplatesPanel: {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.1)',
    padding: '18px',
    overflowX: 'auto',
  },
  inactivityTemplatesTable: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    marginBottom: '14px',
    minWidth: '900px',
  },
  inactivityTemplateHeaderRow: {
    display: 'grid',
    gridTemplateColumns: '120px minmax(220px, 1fr) 110px minmax(220px, 1fr) 100px',
    gap: '12px',
    alignItems: 'center',
    color: '#706A62',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  inactivityTemplateRow: {
    display: 'grid',
    gridTemplateColumns: '120px minmax(220px, 1fr) 110px minmax(220px, 1fr) 100px',
    gap: '12px',
    alignItems: 'center',
  },
  inactivityDayInput: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Mono", monospace',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
  },
  inactivityTemplateInput: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Mono", monospace',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
  },
  inactivityLanguageInput: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Mono", monospace',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
  },
  inactivityParameterInput: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Mono", monospace',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
    resize: 'vertical' as const,
    minHeight: '44px',
  },
  inactivityRemoveButton: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(200, 80, 80, 0.25)',
    backgroundColor: 'rgba(200, 80, 80, 0.08)',
    color: '#A43F3F',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
  },
  inactivityAddButton: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid rgba(61, 58, 54, 0.16)',
    backgroundColor: '#3D3A36',
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 600,
  },
  inactivityTemplateError: {
    margin: '0 0 14px 0',
    color: '#C85050',
    fontSize: '13px',
    fontWeight: 500,
  },
  emptyTemplateText: {
    margin: '0 0 14px 0',
    color: '#9B9285',
    fontSize: '14px',
  },
  // Data Sources Styles
  dataSourcesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  dataSourceItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px',
    borderRadius: '12px',
    border: '2px solid rgba(93, 86, 78, 0.1)',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  dataSourceItemEnabled: {
    border: '2px solid #3D3A36',
    backgroundColor: 'rgba(61, 58, 54, 0.02)',
  },
  dataSourceCheckboxWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '2px',
  },
  dataSourceCheckbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#3D3A36',
  },
  dataSourceContent: {
    flex: 1,
  },
  dataSourceHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  dataSourceIcon: {
    fontSize: '16px',
  },
  dataSourceLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#5C564E',
  },
  dataSourceLabelEnabled: {
    color: '#3D3A36',
  },
  dataSourceDescription: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
    lineHeight: 1.4,
  },
  dataSourcesSelectedInfo: {
    marginTop: '16px',
    fontSize: '13px',
    color: '#706A62',
    margin: '16px 0 0 0',
  },
  // Message Length Styles
  messageLengthContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    maxWidth: '400px',
  },
  messageLengthSelect: {
    padding: '12px 16px',
    borderRadius: '10px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#3D3A36',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.15s ease',
    appearance: 'none' as const,
    backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%235C564E\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '16px',
    paddingRight: '40px',
  },
  messageLengthDescription: {
    fontSize: '13px',
    color: '#706A62',
    margin: 0,
    paddingLeft: '4px',
  },
  // Split Pane Layout Styles
  splitPane: {
    display: 'flex',
    gap: '24px',
    minHeight: '400px',
  },
  editorPane: {
    flex: '1 1 60%',
    display: 'flex',
    flexDirection: 'column' as const,
    background: 'white',
    borderRadius: '16px',
    border: '2px solid rgba(93, 86, 78, 0.08)',
    overflow: 'hidden',
  },
  editorHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    backgroundColor: 'rgba(93, 86, 78, 0.02)',
  },
  editorLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  characterCount: {
    fontSize: '12px',
    color: '#9B9285',
  },
  editor: {
    flex: 1,
    padding: '18px',
    border: 'none',
    outline: 'none',
    resize: 'none' as const,
    fontSize: '14px',
    lineHeight: 1.7,
    fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Mono", monospace',
    color: '#3D3A36',
    backgroundColor: 'transparent',
  },
  editorFooter: {
    padding: '12px 18px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
    backgroundColor: 'rgba(93, 86, 78, 0.02)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  editorHint: {
    fontSize: '12px',
    color: '#9B9285',
  },
  previewLaunchButton: {
    border: '1px solid rgba(47, 107, 95, 0.24)',
    backgroundColor: '#F3FBF7',
    color: '#2F6B5F',
    borderRadius: '10px',
    padding: '9px 14px',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    flexShrink: 0,
  },
  previewLaunchButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  // Sidebar Styles
  sidebarPane: {
    flex: '0 0 320px',
    display: 'flex',
    flexDirection: 'column' as const,
    background: 'white',
    borderRadius: '16px',
    border: '2px solid rgba(93, 86, 78, 0.08)',
    overflow: 'hidden',
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    backgroundColor: 'rgba(93, 86, 78, 0.02)',
  },
  sidebarLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  variableCount: {
    fontSize: '11px',
    color: '#9B9285',
    fontWeight: 500,
  },
  sidebarLoading: {
    padding: '24px',
    textAlign: 'center' as const,
    color: '#9B9285',
    fontSize: '13px',
  },
  variablesList: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '12px',
  },
  variableCategory: {
    marginBottom: '16px',
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 10px',
    marginBottom: '6px',
  },
  categoryIcon: {
    fontSize: '14px',
  },
  categoryLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#5C564E',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  variableItems: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  variableItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    padding: '10px 12px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: 'rgba(93, 86, 78, 0.03)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left' as const,
    transition: 'all 0.15s ease',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  variableItemHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '4px',
  },
  variableName: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#5A6A8A',
    fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Mono", monospace',
  },
  insertIcon: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#9B9285',
    opacity: 0.6,
  },
  variableDescription: {
    fontSize: '11px',
    color: '#706A62',
    lineHeight: 1.4,
  },
  variableExample: {
    fontSize: '10px',
    color: '#9B9285',
    marginTop: '3px',
    fontStyle: 'italic' as const,
  },
  noToolsMessage: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '16px',
    backgroundColor: 'rgba(90, 106, 138, 0.06)',
    borderRadius: '8px',
    marginTop: '8px',
  },
  noToolsIcon: {
    fontSize: '16px',
  },
  noToolsText: {
    fontSize: '12px',
    color: '#5A6A8A',
    lineHeight: 1.5,
  },
  // Preview Pane Styles
  previewPane: {
    flex: '0 0 340px',
    display: 'flex',
    flexDirection: 'column' as const,
    background: 'white',
    borderRadius: '16px',
    border: '2px solid rgba(93, 86, 78, 0.08)',
    overflow: 'hidden',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    backgroundColor: 'rgba(93, 86, 78, 0.02)',
  },
  previewLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  // Send Test Message Button Styles
  sendTestButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    backgroundColor: '#3D3A36',
    border: 'none',
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
    flexShrink: 0,
  },
  sendTestButtonDisabled: {
    backgroundColor: '#C9BBA8',
    cursor: 'not-allowed',
  },
  // Dialog Styles
  dialogOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  dialogContent: {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
  },
  dialogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.1)',
  },
  dialogTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  dialogCloseButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '18px',
    color: '#9B9285',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
  },
  dialogBody: {
    padding: '24px',
  },
  dialogDescription: {
    fontSize: '14px',
    color: '#706A62',
    lineHeight: 1.5,
    marginTop: 0,
    marginBottom: '20px',
  },
  dialogLabel: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#3D3A36',
  },
  dialogInput: {
    padding: '12px 14px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Mono", monospace',
    color: '#3D3A36',
    outline: 'none',
    transition: 'border-color 0.15s ease',
  },
  dialogResult: {
    marginTop: '16px',
    padding: '12px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    lineHeight: 1.5,
  },
  dialogResultSuccess: {
    backgroundColor: 'rgba(90, 138, 94, 0.1)',
    color: '#5A8A5E',
    border: '1px solid rgba(90, 138, 94, 0.2)',
  },
  dialogResultError: {
    backgroundColor: 'rgba(200, 80, 80, 0.1)',
    color: '#C85050',
    border: '1px solid rgba(200, 80, 80, 0.2)',
  },
  dialogFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid rgba(93, 86, 78, 0.1)',
    backgroundColor: 'rgba(93, 86, 78, 0.02)',
  },
  dialogCancelButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(93, 86, 78, 0.2)',
    fontSize: '14px',
    fontWeight: 500,
    color: '#5C564E',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  dialogSendButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    backgroundColor: '#3D3A36',
    border: 'none',
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  dialogSendButtonDisabled: {
    backgroundColor: '#C9BBA8',
    cursor: 'not-allowed',
  },
  // ============== GUIDED CURRICULUM STYLES ==============
  curriculumSettingsContainer: {
    padding: '20px',
    backgroundColor: 'rgba(93, 86, 78, 0.02)',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    marginBottom: '24px',
  },
  curriculumSettingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
  },
  curriculumSettingItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  curriculumSettingLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#5C564E',
  },
  curriculumSettingInput: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
    transition: 'border-color 0.15s ease',
  },
  curriculumSettingSelect: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#3D3A36',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none' as const,
    backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%235C564E\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '16px',
    paddingRight: '40px',
  },
  curriculumSettingTextarea: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
    resize: 'vertical' as const,
    transition: 'border-color 0.15s ease',
  },
  timeCalcToggleContainer: {
    display: 'flex',
    gap: '12px',
  },
  timeCalcToggleButton: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left' as const,
    transition: 'all 0.15s ease',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  timeCalcToggleButtonActive: {
    border: '2px solid #3D3A36',
    backgroundColor: 'rgba(61, 58, 54, 0.04)',
  },
  timeCalcToggleLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  timeCalcToggleDesc: {
    fontSize: '11px',
    color: '#9B9285',
  },
  curriculumEditorContainer: {
    display: 'flex',
    gap: '24px',
    minHeight: '400px',
  },
  curriculumPhasesPanel: {
    flex: '0 0 320px',
    display: 'flex',
    flexDirection: 'column' as const,
    background: 'white',
    borderRadius: '16px',
    border: '2px solid rgba(93, 86, 78, 0.08)',
    overflow: 'hidden',
  },
  curriculumPanelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    backgroundColor: 'rgba(93, 86, 78, 0.02)',
  },
  curriculumPanelTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  curriculumAddButton: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    backgroundColor: 'white',
    fontSize: '12px',
    fontWeight: 500,
    color: '#5C564E',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  curriculumPhasesList: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '12px',
  },
  curriculumPhaseItem: {
    marginBottom: '8px',
  },
  curriculumPhaseHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    borderRadius: '8px',
    backgroundColor: 'rgba(93, 86, 78, 0.05)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  curriculumPhaseHeaderSelected: {
    backgroundColor: 'rgba(61, 58, 54, 0.1)',
    boxShadow: '0 0 0 2px rgba(61, 58, 54, 0.2)',
  },
  curriculumPhaseExpandIcon: {
    fontSize: '10px',
    color: '#9B9285',
    width: '16px',
    flexShrink: 0,
  },
  curriculumPhaseNameInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
    fontFamily: 'inherit',
    outline: 'none',
    minWidth: 0,
  },
  curriculumPhaseTopicCount: {
    fontSize: '11px',
    color: '#9B9285',
    flexShrink: 0,
  },
  curriculumDeleteButton: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '14px',
    color: '#C85050',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.6,
    transition: 'opacity 0.15s ease',
    flexShrink: 0,
  },
  curriculumTopicsList: {
    marginTop: '4px',
    paddingLeft: '24px',
  },
  curriculumPhaseDuration: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    marginBottom: '4px',
  },
  curriculumPhaseDurationLabel: {
    fontSize: '11px',
    color: '#706A62',
  },
  curriculumPhaseDurationInput: {
    width: '60px',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid rgba(93, 86, 78, 0.2)',
    fontSize: '12px',
    fontFamily: 'inherit',
    color: '#3D3A36',
    textAlign: 'center' as const,
  },
  curriculumPhaseDurationUnit: {
    fontSize: '11px',
    color: '#706A62',
  },
  curriculumTopicItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    backgroundColor: 'transparent',
  },
  curriculumTopicItemSelected: {
    backgroundColor: 'rgba(90, 138, 94, 0.1)',
    boxShadow: '0 0 0 2px rgba(90, 138, 94, 0.2)',
  },
  curriculumTopicIcon: {
    fontSize: '14px',
    flexShrink: 0,
  },
  curriculumTopicTitle: {
    flex: 1,
    fontSize: '13px',
    color: '#5C564E',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  curriculumAddTopicButton: {
    width: '100%',
    padding: '8px 12px',
    marginTop: '4px',
    borderRadius: '6px',
    border: '1px dashed rgba(93, 86, 78, 0.2)',
    backgroundColor: 'transparent',
    fontSize: '12px',
    color: '#9B9285',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
    textAlign: 'center' as const,
  },
  curriculumTopicEditorPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    background: 'white',
    borderRadius: '16px',
    border: '2px solid rgba(93, 86, 78, 0.08)',
    overflow: 'hidden',
  },
  curriculumTopicEditorHeader: {
    padding: '14px 18px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    backgroundColor: 'rgba(93, 86, 78, 0.02)',
  },
  curriculumTopicEditorTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  curriculumTopicEditorContent: {
    flex: 1,
    padding: '18px',
    overflowY: 'auto' as const,
  },
  curriculumTopicField: {
    marginBottom: '16px',
  },
  curriculumTopicFieldLabel: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#5C564E',
    marginBottom: '6px',
  },
  curriculumTopicFieldHint: {
    fontSize: '11px',
    fontWeight: 400,
    color: '#9B9285',
  },
  curriculumTopicFieldInput: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s ease',
  },
  curriculumTopicFieldTextarea: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s ease',
  },
  curriculumNoTopicSelected: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    color: '#9B9285',
  },
  curriculumNoTopicIcon: {
    fontSize: '48px',
    opacity: 0.5,
  },
  curriculumNoTopicText: {
    fontSize: '14px',
  },
  // ============== PHASED JOURNEY STYLES ==============
  phaseEditorContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  phaseCard: {
    background: 'white',
    borderRadius: '12px',
    border: '2px solid rgba(93, 86, 78, 0.1)',
    overflow: 'hidden',
  },
  phaseCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: 'rgba(93, 86, 78, 0.03)',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
  },
  phaseCardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
  },
  phaseOrderBadge: {
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    backgroundColor: '#3D3A36',
    color: 'white',
    fontSize: '12px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as React.CSSProperties,
  phaseNameInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
    fontFamily: 'inherit',
    outline: 'none',
    minWidth: 0,
  },
  phaseCardHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  phaseReorderButton: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    backgroundColor: 'white',
    fontSize: '10px',
    color: '#5C564E',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  } as React.CSSProperties,
  phaseReorderButtonDisabled: {
    opacity: 0.3,
    cursor: 'default',
  },
  phaseDeleteButton: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '18px',
    color: '#C85050',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.6,
    transition: 'opacity 0.15s ease',
  } as React.CSSProperties,
  phaseCardBody: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '14px',
  },
  phaseFieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '12px',
  },
  phaseFieldGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  phaseFieldLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#5C564E',
    marginBottom: '2px',
  },
  phaseFieldInput: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s ease',
  },
  phaseFieldFull: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  phaseFieldTextarea: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s ease',
  },
  phaseEstimatedDays: {
    fontSize: '12px',
    color: '#7A7468',
    fontStyle: 'italic' as const,
    padding: '4px 0',
  },
  phaseGoalRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
  },
  phaseGoalInput: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: '8px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#3D3A36',
    backgroundColor: 'white',
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s ease',
  },
  phaseGoalRemoveButton: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: 'rgba(200, 80, 80, 0.1)',
    fontSize: '16px',
    color: '#C85050',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.15s ease',
  } as React.CSSProperties,
  phaseAddGoalButton: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px dashed rgba(93, 86, 78, 0.25)',
    backgroundColor: 'transparent',
    fontSize: '12px',
    fontWeight: 500,
    color: '#5C564E',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
    alignSelf: 'flex-start',
  } as React.CSSProperties,
  phaseAddButton: {
    padding: '12px 20px',
    borderRadius: '10px',
    border: '2px dashed rgba(93, 86, 78, 0.2)',
    backgroundColor: 'transparent',
    fontSize: '14px',
    fontWeight: 500,
    color: '#5C564E',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  groupHeading: {
    marginBottom: '24px',
    paddingBottom: '12px',
    borderBottom: '2px solid rgba(93, 86, 78, 0.12)',
  },
  groupHeadingTitle: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#3D3A36',
    margin: '0 0 6px 0',
  },
  groupHeadingDescription: {
    fontSize: '14px',
    color: '#9B9285',
    margin: 0,
  },
};

export default CommunicationPlanPage;
